import React, { useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { Link as RouterLink, useParams, useNavigate } from "react-router-dom";
import { UserContext } from "../../context/userContext/userContext";
import { useStyle } from "../../context/styleContext/styleContext";
import { ShoppingCartContext } from "../../context/shoppingCartContext/shoppingCartContext";
import { toast } from "react-hot-toast";
import ReactGA from "react-ga4";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import Seo from "../../components/Seo";
import LoginRegisterModal from "../../components/LoginRegisterModal/LoginRegisterModal";
import ProductSchemaMarkup from "./ProductSchemaMarkup";
import { usePromotions } from "../../context/promotionsContext/promotionsContext";
import {
  Box,
  Typography,
  Button,
  useMediaQuery,
  Rating,
  Divider,
  Stack,
  Paper,
  Chip,
  IconButton,
  Card,
  CardContent,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import WindowsRoundedIcon from "@mui/icons-material/DesktopWindowsRounded";
import AppleIcon from "@mui/icons-material/Apple";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import HeadphonesRoundedIcon from "@mui/icons-material/HeadphonesRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import SaleHeroBanner from "../../components/SaleHeroBanner/SaleHeroBanner.jsx"

export default function ProductPage() {
  const { user } = useContext(UserContext);
  const { style } = useStyle();
  const { addToCart } = useContext(ShoppingCartContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { sku } = useParams();
  const navigate = useNavigate();

  const [productData, setProductData] = useState(null);
  const [windowsDownloadRequested, setWindowsDownloadRequested] = useState(false);
  const [macDownloadRequested, setMacDownloadRequested] = useState(false);
  const [isLoginRegisterModalOpen, setLoginRegisterModalOpen] = useState(false);
  const [height, setHeight] = useState(window.innerHeight);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const { getProductDisplayPricing } = usePromotions();
  const productPricing = useMemo(() => {
    if (!productData || Number(productData.price) <= 0) return null;
    return getProductDisplayPricing(productData);
  }, [productData, getProductDisplayPricing]);
  
  const hasPromoDiscount = Boolean(productPricing?.hasDiscount);
  const originalPrice = Number(productPricing?.originalPrice ?? productData?.price ?? 0);
  const finalPrice = Number(productPricing?.finalPrice ?? productData?.price ?? 0);
  const savingsPercent =
    hasPromoDiscount && originalPrice > 0
      ? Math.round((Number(productPricing.discountAmount) / originalPrice) * 100)
      : 0;


  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: sku, title: sku });
    getProductPageInfo();
  }, [sku]);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setSelectedImageIndex(0);
  }, [productData?.productURI]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [sku]);

  const bulletPoints = useMemo(() => {
    if (!productData) return [];
    return [
      productData.bulletPoint1,
      productData.bulletPoint2,
      productData.bulletPoint3,
      productData.bulletPoint4,
      productData.bulletPoint5,
    ].filter(Boolean);
  }, [productData]);

  const galleryImages = useMemo(() => {
    if (!productData?.images) return [];
    const valid = productData.images.filter((img) => img && img.URI);
    if (valid.length <= 1) return valid;
    return valid.slice(0, -1);
  }, [productData]);

  const selectedImage = galleryImages[selectedImageIndex] || galleryImages[0];

  const closeModal = (setModalFn) => () => setModalFn(false);

  const getProductPageInfo = async () => {
    try {
      const { data } = await axios.post("/get_product_page_info", { productURI: sku });
      if (data) {
        setProductData(data);
      }
    } catch (error) {
      console.log("Error retrieving data...", error);
    }
  };

  const addProductToCart = (e) => {
    e.preventDefault();
    addToCart(productData.SKU);
    //toast.success(`${productData.name} added to cart`);
  };

  const downloadWindowsVST = async () => {
    const token = localStorage.getItem("token");

    if (!user) {
      setWindowsDownloadRequested(true);
      setLoginRegisterModalOpen(true);
      return;
    }

    try {
      await axios.post("/api/register_free_product", {
        token,
        sku: productData.SKU,
        productVersion: productData.productVersion,
      });

      window.location.href = productData.windowsDownloadURI;
    } catch (error) {
      toast.error("Error: your request could not be completed at this time.");
    }
  };

  const downloadMacVST = async () => {
    const token = localStorage.getItem("token");

    if (!user) {
      setMacDownloadRequested(true);
      setLoginRegisterModalOpen(true);
      return;
    }

    try {
      await axios.post("/api/register_free_product", {
        token,
        sku: productData.SKU,
        productVersion: productData.productVersion,
      });

      window.location.href = productData.macDownloadURI;
    } catch (error) {
      toast.error("Error: your request could not be completed at this time.");
    }
  };

  const navToSubscribePage = () => {
    navigate("/subscribe");
  };

  const loginCallback = async () => {
    const token = localStorage.getItem("token");
    if (!token || !productData) return;

    setLoginRegisterModalOpen(false);

    try {
      if (windowsDownloadRequested) {
        setWindowsDownloadRequested(false);
        await axios.post("/api/register_free_product", {
          token,
          sku: productData.SKU,
          productVersion: productData.productVersion,
        });
        window.location.href = productData.windowsDownloadURI;
      }

      if (macDownloadRequested) {
        setMacDownloadRequested(false);
        await axios.post("/api/register_free_product", {
          token,
          sku: productData.SKU,
          productVersion: productData.productVersion,
        });
        window.location.href = productData.macDownloadURI;
      }
    } catch (error) {
      toast.error("Error: your request could not be completed at this time.");
    }
  };

  const nextImage = () => {
    if (galleryImages.length < 2) return;
    setSelectedImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const previousImage = () => {
    if (galleryImages.length < 2) return;
    setSelectedImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  if (!productData) {
    return (
      <>
        <Seo
          title="Parish Audio Plugin"
          description="Professional audio plugins by Parish Audio."
          canonical="https://parishaudio.com/products"
        />
        <Box sx={{ minHeight: height - 130 }}>
          <LoadingSpinner />
        </Box>
      </>
    );
  }

  const panelSx = {
    borderRadius: "16px",
    backgroundColor: style.darkPanel,
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow: "none",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  return (
    <>
      <Seo
        title={`${productData.name} VST3 Plugin | Parish Audio`}
        description={`${productData.name} by Parish Audio. ${productData.shortDescription} Available for Mac and Windows as a VST3 plugin.`}
        canonical={`https://parishaudio.com/products/${productData.productURI}`}
        image={
          productData?.images?.[0]?.URI
            ? `https://parishaudio.com${productData.images[0].URI}`
            : "https://parishaudio.com/assets/og-default.jpg"
        }
        imageAlt={
          productData?.images?.[0]?.altText || productData?.name || "Parish Audio plugin"
        }
        ogType="product"
      />

<ProductSchemaMarkup
  productData={productData}
  productPricing={productPricing}
/>

      <Box
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
        <SaleHeroBanner />
        <br />
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              minWidth: 0,
              borderRadius: "20px",
              overflow: "hidden",
              backgroundColor: style.color1,
              border: `1px solid ${style.color2}`,
              boxSizing: "border-box",
            }}
          >
            <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
              {isMobile ? (
                <Stack spacing={1.5}>
                  <Paper elevation={0} sx={{ ...panelSx, p: 1.25 }}>
                    <Stack spacing={1}>
                      <Chip
                        label={productData.price > 0 ? "Premium Plugin" : "Free Plugin"}
                        sx={{
                          alignSelf: "flex-start",
                          borderRadius: "5px",
                          color: style.color4,
                          fontWeight: 700,
                          backgroundColor:
                            productData.price > 0
                              ? "rgba(255,255,255,0.06)"
                              : "rgba(0,180,60,0.18)",
                          border:
                            productData.price > 0
                              ? "1px solid rgba(255,255,255,0.08)"
                              : "1px solid rgba(0,200,80,0.35)",
                        }}
                      />

                      <Typography
                        variant="h1"
                        sx={{
                          fontSize: { xs: "2rem", md: "2.7rem" },
                          lineHeight: 1.04,
                          fontWeight: 800,
                          color: style.color4,
                          letterSpacing: "-0.03em",
                        }}
                      >
                        {productData.name}
                      </Typography>

                      <Typography
                        sx={{
                          color: style.color3,
                          fontSize: "1rem",
                          lineHeight: 1.7,
                        }}
                      >
                        {productData.shortDescription}
                      </Typography>

                      <Box
                        component={RouterLink}
                        to={`/reviews/${productData.productURI}`}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                          width: "fit-content",
                          textDecoration: "none",
                          color: "inherit",
                        }}
                      >
                        <Rating value={productData.starRating || 0} precision={0.5} readOnly />
                        <Typography sx={{ fontWeight: 700, color: style.color4 }}>
                          {productData.ratingCount > 0 ? productData.starRating : ""}
                        </Typography>
                        <Typography sx={{ color: style.color3 }}>
                          ({productData.ratingCount} review{productData.ratingCount === 1 ? "" : "s"})
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>

                  <Paper elevation={0} sx={{ ...panelSx, p: 1.25 }}>
                    <Box
                      sx={{
                        minHeight: 260,
                        height: 260,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(0,0,0,0.18)",
                        borderRadius: "12px",
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      {galleryImages.length > 1 && (
                        <>
                          <IconButton
                            onClick={previousImage}
                            sx={{
                              position: "absolute",
                              left: 8,
                              zIndex: 2,
                              backgroundColor: "rgba(0,0,0,0.32)",
                              color: style.color4,
                              "&:hover": { backgroundColor: "rgba(0,0,0,0.48)" },
                            }}
                          >
                            <ArrowBackIosNewRoundedIcon fontSize="small" />
                          </IconButton>

                          <IconButton
                            onClick={nextImage}
                            sx={{
                              position: "absolute",
                              right: 8,
                              zIndex: 2,
                              backgroundColor: "rgba(0,0,0,0.32)",
                              color: style.color4,
                              "&:hover": { backgroundColor: "rgba(0,0,0,0.48)" },
                            }}
                          >
                            <ArrowForwardIosRoundedIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}

                      <Box
                        component="img"
                        src={selectedImage?.URI}
                        alt={selectedImage?.altText || productData.name}
                        sx={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                          display: "block",
                          p: 1,
                        }}
                      />
                    </Box>

                    {galleryImages.length > 1 && (
                      <Box
                        sx={{
                          mt: 1,
                          display: "grid",
                          gridTemplateColumns: `repeat(${Math.min(galleryImages.length, 4)}, minmax(0, 1fr))`,
                          gap: 1,
                        }}
                      >
                        {galleryImages.map((image, index) => (
                          <Box
                            key={`${image.URI}-${index}`}
                            onClick={() => setSelectedImageIndex(index)}
                            sx={{
                              height: 74,
                              cursor: "pointer",
                              borderRadius: "10px",
                              overflow: "hidden",
                              border:
                                index === selectedImageIndex
                                  ? `2px solid ${style.color2}`
                                  : "2px solid transparent",
                              backgroundColor:
                                index === selectedImageIndex
                                  ? "rgba(255,255,255,0.06)"
                                  : "rgba(0,0,0,0.18)",
                              "&:hover": { borderColor: style.color3 },
                            }}
                          >
                            <Box
                              component="img"
                              src={image.URI}
                              alt={image.altText || `${productData.name} preview ${index + 1}`}
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                display: "block",
                                p: 0.75,
                              }}
                            />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>

                  <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
                    <Stack spacing={1.5}>
                      <Chip
                        label="IN STOCK • INSTANT DOWNLOAD"
                        size="small"
                        sx={{
                          alignSelf: "flex-start",
                          color: style.color4,
                          backgroundColor: "rgba(0,180,60,0.18)",
                          border: "1px solid rgba(0,200,80,0.35)",
                          fontWeight: 700,
                          letterSpacing: 0.2,
                        }}
                      />

                      {productData.price > 0 ? (
                        <>
                          <Typography sx={{ color: style.color3, fontWeight: 700 }}>
                            Purchase this plugin
                          </Typography>

                          {/* <Stack direction="row" spacing={1.25} alignItems="baseline" flexWrap="wrap">
                            <Typography
                              sx={{
                                color: style.color3,
                                textDecoration: "line-through",
                                fontSize: "1.05rem",
                              }}
                            >
                              ${Number(productData.msrp).toFixed(2)}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "2rem",
                                fontWeight: 800,
                                color: style.color4,
                                lineHeight: 1,
                              }}
                            >
                              ${Number(productData.price).toFixed(2)}
                            </Typography>
                          </Stack> */}

                      <Stack spacing={0.8}>
                        {hasPromoDiscount && (
                          <Chip
                            label={`SPRING SALE - Save ${savingsPercent}%`}
                            size="small"
                            sx={{
                              alignSelf: "flex-start",
                              color: "#111",
                              backgroundColor: "#ffcc4d",
                              border: "1px solid rgba(255,255,255,0.18)",
                              fontWeight: 900,
                              letterSpacing: 0.2,
                            }}
                          />
                        )}

                        <Stack direction="row" spacing={1.25} alignItems="baseline" flexWrap="wrap">
                          <Typography
                            sx={{
                              color: style.color3,
                              textDecoration: "line-through",
                              fontSize: "1.05rem",
                            }}
                          >
                            ${hasPromoDiscount ? originalPrice.toFixed(2) : Number(productData.msrp).toFixed(2)}
                          </Typography>

                          {/* {hasPromoDiscount && Number(productData.msrp) > originalPrice && (
                            <Typography
                              sx={{
                                color: "rgba(255,255,255,0.42)",
                                textDecoration: "line-through",
                                fontSize: "0.95rem",
                              }}
                            >
                              ${Number(productData.msrp).toFixed(2)}
                            </Typography>
                          )} */}

                          <Typography
                            sx={{
                              fontSize: "2rem",
                              fontWeight: 900,
                              color: style.color4,
                              lineHeight: 1,
                            }}
                          >
                            ${finalPrice.toFixed(2)}
                          </Typography>
                        </Stack>

                        {hasPromoDiscount && (
                          <Typography sx={{ color: style.color3, fontSize: "0.92rem", lineHeight: 1.45 }}>
                            Sale price applied automatically at checkout.
                          </Typography>
                        )}
                      </Stack>


                          <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
                            One-time purchase. Add this plugin to your cart and own it permanently.
                          </Typography>

                          <Button
                            onClick={addProductToCart}
                            startIcon={<ShoppingCartRoundedIcon />}
                            sx={{
                              borderRadius: "5px",
                              textTransform: "none",
                              fontWeight: 700,
                              color: style.color4,
                              backgroundColor: style.accentGreen,
                              "&:hover": {
                                backgroundColor: style.accentGreen,
                                filter: "brightness(1.05)",
                              },
                            }}
                          >
                            Add to Cart
                          </Button>

                          <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

                          <Stack spacing={0.75}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <AutoAwesomeRoundedIcon sx={{ color: style.color3 }} />
                              <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                                Subscription
                              </Typography>
                            </Stack>

                            <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
                              Try it free for 7 days with an all access subscription.
                            </Typography>

                            <Button
                              href='/subscribe'
                              startIcon={<BoltRoundedIcon />}
                              sx={{
                                borderRadius: "5px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: style.color4,
                                backgroundColor: "rgba(255,255,255,0.05)",
                                border: "1px solid rgba(255,255,255,0.10)",
                                "&:hover": {
                                  backgroundColor: "rgba(255,255,255,0.10)",
                                },
                              }}
                            >
                              Try FREE for 7 Days
                            </Button>
                          </Stack>
                        </>
                      ) : (
                        <>
                          <Typography
                            sx={{
                              fontWeight: 900,
                              fontSize: "2rem",
                              color: "rgb(30,170,60)",
                              lineHeight: 1,
                            }}
                          >
                            FREE
                          </Typography>

                          <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
                            Download this VST3 plugin for Mac or Windows.
                          </Typography>

                          <Stack spacing={1}>
                            {productData.windowsDownloadURI && (
                              <Button
                                onClick={downloadWindowsVST}
                                startIcon={<WindowsRoundedIcon />}
                                sx={{
                                  borderRadius: "5px",
                                  textTransform: "none",
                                  fontWeight: 700,
                                  color: style.color4,
                                  backgroundColor: "rgba(255,255,255,0.05)",
                                  border: "1px solid rgba(255,255,255,0.10)",
                                  "&:hover": {
                                    backgroundColor: "rgba(255,255,255,0.10)",
                                    filter: "brightness(1.05)",
                                  },
                                }}
                              >
                                Download for Windows
                              </Button>
                            )}

                            {productData.macDownloadURI && (
                              <Button
                                onClick={downloadMacVST}
                                startIcon={<AppleIcon />}
                                sx={{
                                  borderRadius: "5px",
                                  textTransform: "none",
                                  fontWeight: 700,
                                  color: style.color4,
                                  backgroundColor: "rgba(255,255,255,0.05)",
                                  border: "1px solid rgba(255,255,255,0.10)",
                                  "&:hover": {
                                    backgroundColor: "rgba(255,255,255,0.10)",
                                    filter: "brightness(1.05)",
                                  },
                                }}
                              >
                                Download for Mac
                              </Button>
                            )}
                          </Stack>
                        </>
                      )}
                    </Stack>
                  </Paper>

                  {productData.audioURIs?.[0] && (
                    <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <HeadphonesRoundedIcon sx={{ color: style.color3 }} />
                          <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
                            Audio Demo
                          </Typography>
                        </Stack>
                        <audio controls style={{ width: "100%", height: "36px" }}>
                          <source src={productData.audioURIs[0]} type="audio/mpeg" />
                        </audio>
                      </Stack>
                    </Paper>
                  )}

{productData.price > 0 && (
                  <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
                    <Stack spacing={1.25}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <DownloadRoundedIcon sx={{ color: style.color3 }} />
                        <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
                          Download Informationzzz
                        </Typography>
                      </Stack>

                      <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
                        Availability: In stock. This product is delivered as an instant digital download.
                      </Typography>

                      {productData.price > 0 && (
                        <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.25 }}>
                        <Stack spacing={1}>
                          <Stack direction="row" spacing={1} alignItems="flex-start">
                            <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
                            <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
                              Download the plugin installer for your device here.
                            </Typography>
                          </Stack>

                          <Stack direction="row" spacing={1} alignItems="flex-start">
                            <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
                            <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
                              To obtain a license, purchase the plugin or start a subscription free trial (can be cancelled any time).
                            </Typography>
                          </Stack>

                          <Stack direction="row" spacing={1} alignItems="flex-start">
                            <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
                            <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
                              After installation, open the plugin in your DAW and sign in with your email address and password to unlock it.
                            </Typography>
                          </Stack>
                        </Stack>
                      </Paper>
                      )}

                      <Stack spacing={1}>
                        {productData.windowsDownloadURI && (
                          <Button
                            component="a"
                            href={productData.windowsDownloadURI}
                            startIcon={<WindowsRoundedIcon />}
                            sx={{
                              borderRadius: "5px",
                              textTransform: "none",
                              fontWeight: 700,
                              color: style.color4,
                              backgroundColor: "rgba(255,255,255,0.05)",
                              border: "1px solid rgba(255,255,255,0.10)",
                              "&:hover": {
                                backgroundColor: "rgba(255,255,255,0.10)",
                              },
                            }}
                          >
                            Windows Download
                          </Button>
                        )}

                        {productData.macDownloadURI && (
                          <Button
                            component="a"
                            href={productData.macDownloadURI}
                            startIcon={<AppleIcon />}
                            sx={{
                              borderRadius: "5px",
                              textTransform: "none",
                              fontWeight: 700,
                              color: style.color4,
                              backgroundColor: "rgba(255,255,255,0.05)",
                              border: "1px solid rgba(255,255,255,0.10)",
                              "&:hover": {
                                backgroundColor: "rgba(255,255,255,0.10)",
                              },
                            }}
                          >
                            Mac Download
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                  </Paper>
                  )}
                </Stack>
              ) : (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "minmax(360px, 0.92fr) minmax(0, 1.08fr)",
                    gap: 2,
                    alignItems: "start",
                  }}
                >
                  <Stack spacing={1.5}>
                    <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
                      <Box
                        sx={{
                          minHeight: 420,
                          height: 420,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "rgba(0,0,0,0.18)",
                          borderRadius: "12px",
                          overflow: "hidden",
                          position: "relative",
                        }}
                      >
                        {galleryImages.length > 1 && (
                          <>
                            <IconButton
                              onClick={previousImage}
                              sx={{
                                position: "absolute",
                                left: 10,
                                zIndex: 2,
                                backgroundColor: "rgba(0,0,0,0.32)",
                                color: style.color4,
                                "&:hover": { backgroundColor: "rgba(0,0,0,0.48)" },
                              }}
                            >
                              <ArrowBackIosNewRoundedIcon fontSize="small" />
                            </IconButton>

                            <IconButton
                              onClick={nextImage}
                              sx={{
                                position: "absolute",
                                right: 10,
                                zIndex: 2,
                                backgroundColor: "rgba(0,0,0,0.32)",
                                color: style.color4,
                                "&:hover": { backgroundColor: "rgba(0,0,0,0.48)" },
                              }}
                            >
                              <ArrowForwardIosRoundedIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}

                        <Box
                          component="img"
                          src={selectedImage?.URI}
                          alt={selectedImage?.altText || productData.name}
                          sx={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            display: "block",
                            p: 1.5,
                          }}
                        />
                      </Box>

                      {galleryImages.length > 1 && (
                        <Box
                          sx={{
                            mt: 1.25,
                            display: "grid",
                            gridTemplateColumns: `repeat(${Math.min(galleryImages.length, 4)}, minmax(0, 1fr))`,
                            gap: 1,
                          }}
                        >
                          {galleryImages.map((image, index) => (
                            <Box
                              key={`${image.URI}-${index}`}
                              onClick={() => setSelectedImageIndex(index)}
                              sx={{
                                height: 82,
                                cursor: "pointer",
                                borderRadius: "10px",
                                overflow: "hidden",
                                border:
                                  index === selectedImageIndex
                                    ? `2px solid ${style.color2}`
                                    : "2px solid transparent",
                                backgroundColor:
                                  index === selectedImageIndex
                                    ? "rgba(255,255,255,0.06)"
                                    : "rgba(0,0,0,0.18)",
                                "&:hover": { borderColor: style.color3 },
                              }}
                            >
                              <Box
                                component="img"
                                src={image.URI}
                                alt={image.altText || `${productData.name} preview ${index + 1}`}
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "contain",
                                  display: "block",
                                  p: 0.75,
                                }}
                              />
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Paper>

                    {productData.audioURIs?.[0] && (
                      <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
                        <Stack spacing={1}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <HeadphonesRoundedIcon sx={{ color: style.color3 }} />
                            <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
                              Audio Demo
                            </Typography>
                          </Stack>
                          <audio controls style={{ width: "100%", height: "36px" }}>
                            <source src={productData.audioURIs[0]} type="audio/mpeg" />
                          </audio>
                        </Stack>
                      </Paper>
                    )}
{productData.price > 0 && (
                    <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
                      <Stack spacing={1.25}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <DownloadRoundedIcon sx={{ color: style.color3 }} />
                          <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
                            Download Information
                          </Typography>
                        </Stack>

                        <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
                          Availability: In stock. This product is delivered as an instant digital download.
                        </Typography>

                        {productData.price > 0 && (
                          <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.25 }}>
                            <Stack spacing={1}>
                              <Stack direction="row" spacing={1} alignItems="flex-start">
                                <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
                                <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
                                  Download the plugin installer for your device here.
                                </Typography>
                              </Stack>

                              <Stack direction="row" spacing={1} alignItems="flex-start">
                                <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
                                <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
                                  To obtain a license, purchase the plugin or start a subscription free trial (can be cancelled any time).
                                </Typography>
                              </Stack>

                              <Stack direction="row" spacing={1} alignItems="flex-start">
                                <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
                                <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
                                  After installation, open the plugin in your DAW and sign in with your email address and password to unlock it.
                                </Typography>
                              </Stack>
                            </Stack>
                          </Paper>
                        )}

                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                          {productData.windowsDownloadURI && (
                            <Button
                              component="a"
                              href={productData.windowsDownloadURI}
                              startIcon={<WindowsRoundedIcon />}
                              sx={{
                                borderRadius: "5px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: style.color4,
                                backgroundColor: "rgba(255,255,255,0.05)",
                                border: "1px solid rgba(255,255,255,0.10)",
                                "&:hover": {
                                  backgroundColor: "rgba(255,255,255,0.10)",
                                },
                                flex: 1,
                              }}
                            >
                              Windows Download
                            </Button>
                          )}

                          {productData.macDownloadURI && (
                            <Button
                              component="a"
                              href={productData.macDownloadURI}
                              startIcon={<AppleIcon />}
                              sx={{
                                borderRadius: "5px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: style.color4,
                                backgroundColor: "rgba(255,255,255,0.05)",
                                border: "1px solid rgba(255,255,255,0.10)",
                                "&:hover": {
                                  backgroundColor: "rgba(255,255,255,0.10)",
                                },
                                flex: 1,
                              }}
                            >
                              Mac Download
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    </Paper>
                    )}
                  </Stack>

                  <Stack spacing={1.5}>
                    <Paper elevation={0} sx={{ ...panelSx, p: 1.75 }}>
                      <Stack spacing={1}>
                        <Chip
                          label={productData.price > 0 ? "Premium Plugin" : "Free Plugin"}
                          sx={{
                            alignSelf: "flex-start",
                            borderRadius: "5px",
                            color: style.color4,
                            fontWeight: 700,
                            backgroundColor:
                              productData.price > 0
                                ? "rgba(255,255,255,0.06)"
                                : "rgba(0,180,60,0.18)",
                            border:
                              productData.price > 0
                                ? "1px solid rgba(255,255,255,0.08)"
                                : "1px solid rgba(0,200,80,0.35)",
                          }}
                        />

                        <Typography
                          variant="h1"
                          sx={{
                            fontSize: { xs: "2rem", md: "2.65rem" },
                            lineHeight: 1.04,
                            fontWeight: 800,
                            color: style.color4,
                            letterSpacing: "-0.03em",
                          }}
                        >
                          {productData.name}
                        </Typography>

                        <Typography
                          sx={{
                            color: style.color3,
                            fontSize: { xs: "1rem", md: "1.03rem" },
                            lineHeight: 1.7,
                          }}
                        >
                          {productData.shortDescription}
                        </Typography>

                        <Box
                          component={RouterLink}
                          to={`/reviews/${productData.productURI}`}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                            width: "fit-content",
                            textDecoration: "none",
                            color: "inherit",
                            pt: 0.25,
                          }}
                        >
                          <Rating value={productData.starRating || 0} precision={0.5} readOnly />
                          <Typography sx={{ fontWeight: 700, color: style.color4 }}>
                            {productData.ratingCount > 0 ? productData.starRating : ""}
                          </Typography>
                          <Typography sx={{ color: style.color3 }}>
                            ({productData.ratingCount} review{productData.ratingCount === 1 ? "" : "s"})
                          </Typography>
                        </Box>
                      </Stack>

                      <Divider sx={{ my: 1.5, backgroundColor: "rgba(255,255,255,0.08)" }} />

                      <Stack spacing={1.5}>
                        <Chip
                          label="IN STOCK • INSTANT DOWNLOAD"
                          size="small"
                          sx={{
                            alignSelf: "flex-start",
                            color: style.color4,
                            backgroundColor: "rgba(0,180,60,0.18)",
                            border: "1px solid rgba(0,200,80,0.35)",
                            fontWeight: 700,
                            letterSpacing: 0.2,
                          }}
                        />

                        {productData.price > 0 ? (
                          <>
                            <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                              Purchase this plugin
                            </Typography>
                            <Stack spacing={0.8}>
                                {hasPromoDiscount && (
                                  <Chip
                                    label={`SPRING SALE - Save ${savingsPercent}%`}
                                    size="small"
                                    sx={{
                                      alignSelf: "flex-start",
                                      color: "#111",
                                      backgroundColor: "#ffcc4d",
                                      border: "1px solid rgba(255,255,255,0.18)",
                                      fontWeight: 900,
                                      letterSpacing: 0.2,
                                    }}
                                  />
                                )}

                                <Stack direction="row" spacing={1.25} alignItems="baseline" flexWrap="wrap">
                                  <Typography
                                    sx={{
                                      color: style.color3,
                                      textDecoration: "line-through",
                                      fontSize: "1.05rem",
                                    }}
                                  >
                                    ${hasPromoDiscount ? originalPrice.toFixed(2) : Number(productData.msrp).toFixed(2)}
                                  </Typography>

                                  <Typography
                                    sx={{
                                      fontSize: "2rem",
                                      fontWeight: 900,
                                      color: style.color4,
                                      lineHeight: 1,
                                    }}
                                  >
                                    ${finalPrice.toFixed(2)}
                                  </Typography>
                                </Stack>

                                {hasPromoDiscount && (
                                  <Typography sx={{ color: style.color3, fontSize: "0.92rem", lineHeight: 1.45 }}>
                                    Sale price applied automatically at checkout.
                                  </Typography>
                                )}
                              </Stack>

                            {/* <Stack direction="row" spacing={1.5} alignItems="baseline" flexWrap="wrap">
                              <Typography
                                sx={{
                                  color: style.color3,
                                  textDecoration: "line-through",
                                  fontSize: "1.15rem",
                                }}
                              >
                                ${Number(productData.msrp).toFixed(2)}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "2.1rem",
                                  fontWeight: 800,
                                  color: style.color4,
                                  lineHeight: 1,
                                }}
                              >
                                ${Number(productData.price).toFixed(2)}
                              </Typography>
                            </Stack> */}

                            <Typography sx={{ color: style.color3, lineHeight: 1.65 }}>
                              One-time purchase. Add this plugin to your cart and own it permanently.
                            </Typography>

                            <Button
                              onClick={addProductToCart}
                              startIcon={<ShoppingCartRoundedIcon />}
                              sx={{
                                alignSelf: "flex-start",
                                borderRadius: "5px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: style.color4,
                                backgroundColor: style.accentGreen,
                                px: 2.5,
                                "&:hover": {
                                  backgroundColor: style.accentGreen,
                                  filter: "brightness(1.05)",
                                },
                              }}
                            >
                              Add to Cart
                            </Button>

                            <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

                            <Stack spacing={0.8}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <AutoAwesomeRoundedIcon sx={{ color: style.color3 }} />
                                <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                                  Subscription
                                </Typography>
                              </Stack>

                              <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
                                Try it free for 7 days with an all access subscription.
                              </Typography>

                              <Button
                                href='/subscribe'
                                startIcon={<BoltRoundedIcon />}
                                sx={{
                                  alignSelf: "flex-start",
                                  borderRadius: "5px",
                                  textTransform: "none",
                                  fontWeight: 700,
                                  color: style.color4,
                                  backgroundColor: "rgba(255,255,255,0.05)",
                                  border: "1px solid rgba(255,255,255,0.10)",
                                  px: 2.5,
                                  "&:hover": {
                                    backgroundColor: "rgba(255,255,255,0.10)",
                                  },
                                }}
                              >
                                Try FREE for 7 Days
                              </Button>

                              <Typography sx={{ color: style.color3, fontSize: "0.92rem" }}>
                                $9.95 per month for access to all plugins. Cancel anytime.
                              </Typography>
                            </Stack>
                          </>
                        ) : (
                          <>
                            <Typography
                              sx={{
                                fontWeight: 900,
                                fontSize: "2.2rem",
                                color: "rgb(30,170,60)",
                                lineHeight: 1,
                              }}
                            >
                              FREE
                            </Typography>

                            <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
                              Download this VST3 plugin for Mac or Windows.
                            </Typography>

                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                              {productData.windowsDownloadURI && (
                                <Button
                                  onClick={downloadWindowsVST}
                                  startIcon={<WindowsRoundedIcon />}
                                  sx={{
                                    borderRadius: "5px",
                                    textTransform: "none",
                                    fontWeight: 700,
                                    color: style.color4,
                                    backgroundColor: "rgba(255,255,255,0.05)",
                                    border: "1px solid rgba(255,255,255,0.10)",
                                    "&:hover": {
                                      backgroundColor: "rgba(255,255,255,0.10)",
                                      filter: "brightness(1.05)",
                                    },
                                  }}
                                >
                                  Download for Windows
                                </Button>
                              )}

                              {productData.macDownloadURI && (
                                <Button
                                  onClick={downloadMacVST}
                                  startIcon={<AppleIcon />}
                                  sx={{
                                    borderRadius: "5px",
                                    textTransform: "none",
                                    fontWeight: 700,
                                    color: style.color4,
                                    backgroundColor: "rgba(255,255,255,0.05)",
                                    border: "1px solid rgba(255,255,255,0.10)",
                                    "&:hover": {
                                      backgroundColor: "rgba(255,255,255,0.10)",
                                      filter: "brightness(1.05)",
                                    },
                                  }}
                                >
                                  Download for Mac
                                </Button>
                              )}
                            </Stack>
                          </>
                        )}
                      </Stack>
                    </Paper>
                  </Stack>
                </Box>
              )}

              <Box sx={{ mt: 1.5 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1.2fr 0.8fr" },
                    gap: 2,
                    alignItems: "stretch",
                  }}
                >
                  <Paper elevation={0} sx={{ ...panelSx, p: { xs: 1.5, md: 2 } }}>
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LibraryMusicRoundedIcon sx={{ color: style.color3 }} />
                        <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: style.color4 }}>
                          Overview
                        </Typography>
                      </Stack>

                      <Typography
                        sx={{
                          color: style.color4,
                          lineHeight: 1.75,
                          whiteSpace: "pre-line",
                          fontSize: "1rem",
                        }}
                      >
                        {productData.longDescription}
                      </Typography>

                      {bulletPoints.length > 0 && (
                        <>
                          <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

                          <Stack spacing={1}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <InfoOutlinedIcon sx={{ color: style.color3 }} />
                              <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
                                Highlights
                              </Typography>
                            </Stack>

                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                gap: 1.1,
                              }}
                            >
                              {bulletPoints.map((point, idx) => (
                                <Paper
                                  key={idx}
                                  elevation={0}
                                  sx={{
                                    ...subtlePanelSx,
                                    p: 1.15,
                                    minHeight: 25,
                                    display: "flex",
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <Stack direction="row" spacing={1} alignItems="flex-start">
                                    <CheckCircleRoundedIcon
                                      sx={{
                                        color: style.color3,
                                        mt: "2px",
                                        fontSize: "1.05rem",
                                        flexShrink: 0,
                                      }}
                                    />
                                    <Typography
                                      sx={{
                                        color: style.color4,
                                        lineHeight: 1.5,
                                        fontSize: "0.92rem",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {point}
                                    </Typography>
                                  </Stack>
                                </Paper>
                              ))}
                            </Box>
                          </Stack>
                        </>
                      )}
                    </Stack>
                  </Paper>

                  <Paper elevation={0} sx={{ ...panelSx, p: { xs: 1.5, md: 2 } }}>
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TuneRoundedIcon sx={{ color: style.color3 }} />
                        <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: style.color4 }}>
                          Controls
                        </Typography>
                      </Stack>

                      {productData.controls?.length > 0 ? (
                        <Stack spacing={1.1}>
                          {productData.controls.map((control, index) => (
                            <Card
                              key={index}
                              elevation={0}
                              sx={{
                                backgroundColor: "rgba(0,0,0,0.18)",
                                color: style.color4,
                                borderRadius: "12px",
                                border: "1px solid rgba(255,255,255,0.06)",
                                boxShadow: "none",
                              }}
                            >
                              <CardContent sx={{ p: "14px !important" }}>
                                <Typography
                                  sx={{
                                    fontWeight: 800,
                                    color: style.color4,
                                    mb: 0.4,
                                  }}
                                >
                                  {control.name}
                                </Typography>
                                <Typography
                                  sx={{
                                    color: style.color3,
                                    lineHeight: 1.55,
                                    fontSize: "0.92rem",
                                  }}
                                >
                                  {control.description}
                                </Typography>
                              </CardContent>
                            </Card>
                          ))}
                        </Stack>
                      ) : (
                        <Typography sx={{ color: style.color3 }}>
                          No control information available.
                        </Typography>
                      )}
                    </Stack>
                  </Paper>
                </Box>
              </Box>

              <Divider sx={{ my: 2, backgroundColor: "rgba(255,255,255,0.08)" }} />

              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
              >
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography sx={{ color: style.color3, fontSize: "0.92rem" }}>
                    Format:
                  </Typography>
                  <Chip
                    label="VST3"
                    size="small"
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.08)",
                      color: style.color4,
                    }}
                  />
                  <Chip
                    label="Mac"
                    size="small"
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.08)",
                      color: style.color4,
                    }}
                  />
                  <Chip
                    label="Windows"
                    size="small"
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.08)",
                      color: style.color4,
                    }}
                  />
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button
                    component={RouterLink}
                    to={`/reviews/${productData.productURI}`}
                    variant="text"
                    sx={{
                      color: style.color3,
                      fontWeight: 700,
                      px: 0,
                      "&:hover": {
                        backgroundColor: "transparent",
                        color: style.color4,
                      },
                    }}
                  >
                    Read all reviews
                  </Button>

                  <Button
                    component={RouterLink}
                    to="/plugins"
                    variant="text"
                    sx={{
                      color: style.color3,
                      fontWeight: 700,
                      px: 0,
                      "&:hover": {
                        backgroundColor: "transparent",
                        color: style.color4,
                      },
                    }}
                  >
                    Browse all plugins
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Box>

      <LoginRegisterModal
        isOpen={isLoginRegisterModalOpen}
        close={closeModal(setLoginRegisterModalOpen)}
        loginComplete={loginCallback}
      />
    </>
  );
}

// import React, { useState, useContext, useEffect, useMemo } from "react";
// import axios from "axios";
// import { Link as RouterLink, useParams, useNavigate } from "react-router-dom";
// import { UserContext } from "../../context/userContext/userContext";
// import { useStyle } from "../../context/styleContext/styleContext";
// import { ShoppingCartContext } from "../../context/shoppingCartContext/shoppingCartContext";
// import { toast } from "react-hot-toast";
// import ReactGA from "react-ga4";
// import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
// import Seo from "../../components/Seo";
// import LoginRegisterModal from "../../components/LoginRegisterModal/LoginRegisterModal";
// import ProductSchemaMarkup from "./ProductSchemaMarkup";
// import { usePromotions } from "../../context/promotionsContext/promotionsContext";
// import {
//   Box,
//   Typography,
//   Button,
//   useMediaQuery,
//   Rating,
//   Divider,
//   Stack,
//   Paper,
//   Chip,
//   IconButton,
//   Card,
//   CardContent,
// } from "@mui/material";
// import { useTheme } from "@mui/material/styles";
// import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
// import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
// import WindowsRoundedIcon from "@mui/icons-material/DesktopWindowsRounded";
// import AppleIcon from "@mui/icons-material/Apple";
// import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
// import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
// import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
// import HeadphonesRoundedIcon from "@mui/icons-material/HeadphonesRounded";
// import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
// import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
// import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
// import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
// import CheckCircleIcon from "@mui/icons-material/CheckCircle";
// import BoltRoundedIcon from "@mui/icons-material/BoltRounded";

// export default function ProductPage() {
//   const { user } = useContext(UserContext);
//   const { style } = useStyle();
//   const { addToCart } = useContext(ShoppingCartContext);
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("md"));
//   const { sku } = useParams();
//   const navigate = useNavigate();

//   const [productData, setProductData] = useState(null);
//   const [windowsDownloadRequested, setWindowsDownloadRequested] = useState(false);
//   const [macDownloadRequested, setMacDownloadRequested] = useState(false);
//   const [isLoginRegisterModalOpen, setLoginRegisterModalOpen] = useState(false);
//   const [height, setHeight] = useState(window.innerHeight);
//   const [selectedImageIndex, setSelectedImageIndex] = useState(0);
//   const { getProductDisplayPricing } = usePromotions();
//   const productPricing = useMemo(() => {
//     if (!productData || Number(productData.price) <= 0) return null;
//     return getProductDisplayPricing(productData);
//   }, [productData, getProductDisplayPricing]);
  
//   const hasPromoDiscount = Boolean(productPricing?.hasDiscount);
//   const originalPrice = Number(productPricing?.originalPrice ?? productData?.price ?? 0);
//   const finalPrice = Number(productPricing?.finalPrice ?? productData?.price ?? 0);
//   const savingsPercent =
//     hasPromoDiscount && originalPrice > 0
//       ? Math.round((Number(productPricing.discountAmount) / originalPrice) * 100)
//       : 0;


//   useEffect(() => {
//     ReactGA.send({ hitType: "pageview", page: sku, title: sku });
//     getProductPageInfo();
//   }, [sku]);

//   useEffect(() => {
//     function handleResize() {
//       setHeight(window.innerHeight);
//     }
//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   useEffect(() => {
//     setSelectedImageIndex(0);
//   }, [productData?.productURI]);

//   useEffect(() => {
//     window.scrollTo({ top: 0, left: 0, behavior: "auto" });
//   }, [sku]);

//   const bulletPoints = useMemo(() => {
//     if (!productData) return [];
//     return [
//       productData.bulletPoint1,
//       productData.bulletPoint2,
//       productData.bulletPoint3,
//       productData.bulletPoint4,
//       productData.bulletPoint5,
//     ].filter(Boolean);
//   }, [productData]);

//   const galleryImages = useMemo(() => {
//     if (!productData?.images) return [];
//     const valid = productData.images.filter((img) => img && img.URI);
//     if (valid.length <= 1) return valid;
//     return valid.slice(0, -1);
//   }, [productData]);

//   const selectedImage = galleryImages[selectedImageIndex] || galleryImages[0];

//   const closeModal = (setModalFn) => () => setModalFn(false);

//   const getProductPageInfo = async () => {
//     try {
//       const { data } = await axios.post("/get_product_page_info", { productURI: sku });
//       if (data) {
//         setProductData(data);
//       }
//     } catch (error) {
//       console.log("Error retrieving data...", error);
//     }
//   };

//   const addProductToCart = (e) => {
//     e.preventDefault();
//     addToCart(productData.SKU);
//     //toast.success(`${productData.name} added to cart`);
//   };

//   const downloadWindowsVST = async () => {
//     const token = localStorage.getItem("token");

//     if (!user) {
//       setWindowsDownloadRequested(true);
//       setLoginRegisterModalOpen(true);
//       return;
//     }

//     try {
//       await axios.post("/api/register_free_product", {
//         token,
//         sku: productData.SKU,
//         productVersion: productData.productVersion,
//       });

//       window.location.href = productData.windowsDownloadURI;
//     } catch (error) {
//       toast.error("Error: your request could not be completed at this time.");
//     }
//   };

//   const downloadMacVST = async () => {
//     const token = localStorage.getItem("token");

//     if (!user) {
//       setMacDownloadRequested(true);
//       setLoginRegisterModalOpen(true);
//       return;
//     }

//     try {
//       await axios.post("/api/register_free_product", {
//         token,
//         sku: productData.SKU,
//         productVersion: productData.productVersion,
//       });

//       window.location.href = productData.macDownloadURI;
//     } catch (error) {
//       toast.error("Error: your request could not be completed at this time.");
//     }
//   };

//   const navToSubscribePage = () => {
//     navigate("/subscribe");
//   };

//   const loginCallback = async () => {
//     const token = localStorage.getItem("token");
//     if (!token || !productData) return;

//     setLoginRegisterModalOpen(false);

//     try {
//       if (windowsDownloadRequested) {
//         setWindowsDownloadRequested(false);
//         await axios.post("/api/register_free_product", {
//           token,
//           sku: productData.SKU,
//           productVersion: productData.productVersion,
//         });
//         window.location.href = productData.windowsDownloadURI;
//       }

//       if (macDownloadRequested) {
//         setMacDownloadRequested(false);
//         await axios.post("/api/register_free_product", {
//           token,
//           sku: productData.SKU,
//           productVersion: productData.productVersion,
//         });
//         window.location.href = productData.macDownloadURI;
//       }
//     } catch (error) {
//       toast.error("Error: your request could not be completed at this time.");
//     }
//   };

//   const nextImage = () => {
//     if (galleryImages.length < 2) return;
//     setSelectedImageIndex((prev) => (prev + 1) % galleryImages.length);
//   };

//   const previousImage = () => {
//     if (galleryImages.length < 2) return;
//     setSelectedImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
//   };

//   if (!productData) {
//     return (
//       <>
//         <Seo
//           title="Parish Audio Plugin"
//           description="Professional audio plugins by Parish Audio."
//           canonical="https://parishaudio.com/products"
//         />
//         <Box sx={{ minHeight: height - 130 }}>
//           <LoadingSpinner />
//         </Box>
//       </>
//     );
//   }

//   const panelSx = {
//     borderRadius: "16px",
//     backgroundColor: style.darkPanel,
//     border: "1px solid rgba(255,255,255,0.08)",
//     boxShadow: "none",
//   };

//   const subtlePanelSx = {
//     borderRadius: "14px",
//     backgroundColor: style.darkerPanel,
//     border: "1px solid rgba(255,255,255,0.06)",
//     boxShadow: "none",
//   };

//   return (
//     <>
//       <Seo
//         title={`${productData.name} VST3 Plugin | Parish Audio`}
//         description={`${productData.name} by Parish Audio. ${productData.shortDescription} Available for Mac and Windows as a VST3 plugin.`}
//         canonical={`https://parishaudio.com/products/${productData.productURI}`}
//         image={
//           productData?.images?.[0]?.URI
//             ? `https://parishaudio.com${productData.images[0].URI}`
//             : "https://parishaudio.com/assets/og-default.jpg"
//         }
//         imageAlt={
//           productData?.images?.[0]?.altText || productData?.name || "Parish Audio plugin"
//         }
//         ogType="product"
//       />

// <ProductSchemaMarkup
//   productData={productData}
//   productPricing={productPricing}
// />

//       <Box
//         sx={{
//           width: "100%",
//           maxWidth: "100vw",
//           overflowX: "hidden",
//           backgroundColor: "black",
//           px: { xs: 1.25, sm: 1.5, md: 2.5 },
//           py: { xs: 1.5, md: 2.5 },
//           boxSizing: "border-box",
//         }}
//       >
//         <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
//           <Paper
//             elevation={0}
//             sx={{
//               width: "100%",
//               minWidth: 0,
//               borderRadius: "20px",
//               overflow: "hidden",
//               backgroundColor: style.color1,
//               border: `1px solid ${style.color2}`,
//               boxSizing: "border-box",
//             }}
//           >
//             <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
//               {isMobile ? (
//                 <Stack spacing={1.5}>
//                   <Paper elevation={0} sx={{ ...panelSx, p: 1.25 }}>
//                     <Stack spacing={1}>
//                       <Chip
//                         label={productData.price > 0 ? "Premium Plugin" : "Free Plugin"}
//                         sx={{
//                           alignSelf: "flex-start",
//                           borderRadius: "5px",
//                           color: style.color4,
//                           fontWeight: 700,
//                           backgroundColor:
//                             productData.price > 0
//                               ? "rgba(255,255,255,0.06)"
//                               : "rgba(0,180,60,0.18)",
//                           border:
//                             productData.price > 0
//                               ? "1px solid rgba(255,255,255,0.08)"
//                               : "1px solid rgba(0,200,80,0.35)",
//                         }}
//                       />

//                       <Typography
//                         variant="h1"
//                         sx={{
//                           fontSize: { xs: "2rem", md: "2.7rem" },
//                           lineHeight: 1.04,
//                           fontWeight: 800,
//                           color: style.color4,
//                           letterSpacing: "-0.03em",
//                         }}
//                       >
//                         {productData.name}
//                       </Typography>

//                       <Typography
//                         sx={{
//                           color: style.color3,
//                           fontSize: "1rem",
//                           lineHeight: 1.7,
//                         }}
//                       >
//                         {productData.shortDescription}
//                       </Typography>

//                       <Box
//                         component={RouterLink}
//                         to={`/reviews/${productData.productURI}`}
//                         sx={{
//                           display: "inline-flex",
//                           alignItems: "center",
//                           gap: 1,
//                           flexWrap: "wrap",
//                           width: "fit-content",
//                           textDecoration: "none",
//                           color: "inherit",
//                         }}
//                       >
//                         <Rating value={productData.starRating || 0} precision={0.5} readOnly />
//                         <Typography sx={{ fontWeight: 700, color: style.color4 }}>
//                           {productData.ratingCount > 0 ? productData.starRating : ""}
//                         </Typography>
//                         <Typography sx={{ color: style.color3 }}>
//                           ({productData.ratingCount} review{productData.ratingCount === 1 ? "" : "s"})
//                         </Typography>
//                       </Box>
//                     </Stack>
//                   </Paper>

//                   <Paper elevation={0} sx={{ ...panelSx, p: 1.25 }}>
//                     <Box
//                       sx={{
//                         minHeight: 260,
//                         height: 260,
//                         display: "flex",
//                         alignItems: "center",
//                         justifyContent: "center",
//                         backgroundColor: "rgba(0,0,0,0.18)",
//                         borderRadius: "12px",
//                         overflow: "hidden",
//                         position: "relative",
//                       }}
//                     >
//                       {galleryImages.length > 1 && (
//                         <>
//                           <IconButton
//                             onClick={previousImage}
//                             sx={{
//                               position: "absolute",
//                               left: 8,
//                               zIndex: 2,
//                               backgroundColor: "rgba(0,0,0,0.32)",
//                               color: style.color4,
//                               "&:hover": { backgroundColor: "rgba(0,0,0,0.48)" },
//                             }}
//                           >
//                             <ArrowBackIosNewRoundedIcon fontSize="small" />
//                           </IconButton>

//                           <IconButton
//                             onClick={nextImage}
//                             sx={{
//                               position: "absolute",
//                               right: 8,
//                               zIndex: 2,
//                               backgroundColor: "rgba(0,0,0,0.32)",
//                               color: style.color4,
//                               "&:hover": { backgroundColor: "rgba(0,0,0,0.48)" },
//                             }}
//                           >
//                             <ArrowForwardIosRoundedIcon fontSize="small" />
//                           </IconButton>
//                         </>
//                       )}

//                       <Box
//                         component="img"
//                         src={selectedImage?.URI}
//                         alt={selectedImage?.altText || productData.name}
//                         sx={{
//                           maxWidth: "100%",
//                           maxHeight: "100%",
//                           objectFit: "contain",
//                           display: "block",
//                           p: 1,
//                         }}
//                       />
//                     </Box>

//                     {galleryImages.length > 1 && (
//                       <Box
//                         sx={{
//                           mt: 1,
//                           display: "grid",
//                           gridTemplateColumns: `repeat(${Math.min(galleryImages.length, 4)}, minmax(0, 1fr))`,
//                           gap: 1,
//                         }}
//                       >
//                         {galleryImages.map((image, index) => (
//                           <Box
//                             key={`${image.URI}-${index}`}
//                             onClick={() => setSelectedImageIndex(index)}
//                             sx={{
//                               height: 74,
//                               cursor: "pointer",
//                               borderRadius: "10px",
//                               overflow: "hidden",
//                               border:
//                                 index === selectedImageIndex
//                                   ? `2px solid ${style.color2}`
//                                   : "2px solid transparent",
//                               backgroundColor:
//                                 index === selectedImageIndex
//                                   ? "rgba(255,255,255,0.06)"
//                                   : "rgba(0,0,0,0.18)",
//                               "&:hover": { borderColor: style.color3 },
//                             }}
//                           >
//                             <Box
//                               component="img"
//                               src={image.URI}
//                               alt={image.altText || `${productData.name} preview ${index + 1}`}
//                               sx={{
//                                 width: "100%",
//                                 height: "100%",
//                                 objectFit: "contain",
//                                 display: "block",
//                                 p: 0.75,
//                               }}
//                             />
//                           </Box>
//                         ))}
//                       </Box>
//                     )}
//                   </Paper>

//                   <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
//                     <Stack spacing={1.5}>
//                       <Chip
//                         label="IN STOCK • INSTANT DOWNLOAD"
//                         size="small"
//                         sx={{
//                           alignSelf: "flex-start",
//                           color: style.color4,
//                           backgroundColor: "rgba(0,180,60,0.18)",
//                           border: "1px solid rgba(0,200,80,0.35)",
//                           fontWeight: 700,
//                           letterSpacing: 0.2,
//                         }}
//                       />

//                       {productData.price > 0 ? (
//                         <>
//                           <Typography sx={{ color: style.color3, fontWeight: 700 }}>
//                             Purchase this plugin
//                           </Typography>

//                           {/* <Stack direction="row" spacing={1.25} alignItems="baseline" flexWrap="wrap">
//                             <Typography
//                               sx={{
//                                 color: style.color3,
//                                 textDecoration: "line-through",
//                                 fontSize: "1.05rem",
//                               }}
//                             >
//                               ${Number(productData.msrp).toFixed(2)}
//                             </Typography>
//                             <Typography
//                               sx={{
//                                 fontSize: "2rem",
//                                 fontWeight: 800,
//                                 color: style.color4,
//                                 lineHeight: 1,
//                               }}
//                             >
//                               ${Number(productData.price).toFixed(2)}
//                             </Typography>
//                           </Stack> */}

//                       <Stack spacing={0.8}>
//                         {hasPromoDiscount && (
//                           <Chip
//                             label={`FLASH SALE - Save ${savingsPercent}%`}
//                             size="small"
//                             sx={{
//                               alignSelf: "flex-start",
//                               color: "#111",
//                               backgroundColor: "#ffcc4d",
//                               border: "1px solid rgba(255,255,255,0.18)",
//                               fontWeight: 900,
//                               letterSpacing: 0.2,
//                             }}
//                           />
//                         )}

//                         <Stack direction="row" spacing={1.25} alignItems="baseline" flexWrap="wrap">
//                           <Typography
//                             sx={{
//                               color: style.color3,
//                               textDecoration: "line-through",
//                               fontSize: "1.05rem",
//                             }}
//                           >
//                             ${hasPromoDiscount ? originalPrice.toFixed(2) : Number(productData.msrp).toFixed(2)}
//                           </Typography>

//                           {/* {hasPromoDiscount && Number(productData.msrp) > originalPrice && (
//                             <Typography
//                               sx={{
//                                 color: "rgba(255,255,255,0.42)",
//                                 textDecoration: "line-through",
//                                 fontSize: "0.95rem",
//                               }}
//                             >
//                               ${Number(productData.msrp).toFixed(2)}
//                             </Typography>
//                           )} */}

//                           <Typography
//                             sx={{
//                               fontSize: "2rem",
//                               fontWeight: 900,
//                               color: style.color4,
//                               lineHeight: 1,
//                             }}
//                           >
//                             ${finalPrice.toFixed(2)}
//                           </Typography>
//                         </Stack>

//                         {hasPromoDiscount && (
//                           <Typography sx={{ color: style.color3, fontSize: "0.92rem", lineHeight: 1.45 }}>
//                             Sale price applied automatically at checkout.
//                           </Typography>
//                         )}
//                       </Stack>


//                           <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
//                             One-time purchase. Add this plugin to your cart and own it permanently.
//                           </Typography>

//                           <Button
//                             onClick={addProductToCart}
//                             startIcon={<ShoppingCartRoundedIcon />}
//                             sx={{
//                               borderRadius: "5px",
//                               textTransform: "none",
//                               fontWeight: 700,
//                               color: style.color4,
//                               backgroundColor: style.accentGreen,
//                               "&:hover": {
//                                 backgroundColor: style.accentGreen,
//                                 filter: "brightness(1.05)",
//                               },
//                             }}
//                           >
//                             Add to Cart
//                           </Button>

//                           <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

//                           <Stack spacing={0.75}>
//                             <Stack direction="row" spacing={1} alignItems="center">
//                               <AutoAwesomeRoundedIcon sx={{ color: style.color3 }} />
//                               <Typography sx={{ color: style.color4, fontWeight: 700 }}>
//                                 Subscription
//                               </Typography>
//                             </Stack>

//                             <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
//                               Try it free for 7 days with an all access subscription.
//                             </Typography>

//                             <Button
//                               href='/subscribe'
//                               startIcon={<BoltRoundedIcon />}
//                               sx={{
//                                 borderRadius: "5px",
//                                 textTransform: "none",
//                                 fontWeight: 700,
//                                 color: style.color4,
//                                 backgroundColor: "rgba(255,255,255,0.05)",
//                                 border: "1px solid rgba(255,255,255,0.10)",
//                                 "&:hover": {
//                                   backgroundColor: "rgba(255,255,255,0.10)",
//                                 },
//                               }}
//                             >
//                               Try FREE for 7 Days
//                             </Button>
//                           </Stack>
//                         </>
//                       ) : (
//                         <>
//                           <Typography
//                             sx={{
//                               fontWeight: 900,
//                               fontSize: "2rem",
//                               color: "rgb(30,170,60)",
//                               lineHeight: 1,
//                             }}
//                           >
//                             FREE
//                           </Typography>

//                           <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
//                             Download this VST3 plugin for Mac or Windows.
//                           </Typography>

//                           <Stack spacing={1}>
//                             {productData.windowsDownloadURI && (
//                               <Button
//                                 onClick={downloadWindowsVST}
//                                 startIcon={<WindowsRoundedIcon />}
//                                 sx={{
//                                   borderRadius: "5px",
//                                   textTransform: "none",
//                                   fontWeight: 700,
//                                   color: style.color4,
//                                   backgroundColor: style.accentGreen,
//                                   "&:hover": {
//                                     backgroundColor: style.accentGreen,
//                                     filter: "brightness(1.05)",
//                                   },
//                                 }}
//                               >
//                                 Download for Windows
//                               </Button>
//                             )}

//                             {productData.macDownloadURI && (
//                               <Button
//                                 onClick={downloadMacVST}
//                                 startIcon={<AppleIcon />}
//                                 sx={{
//                                   borderRadius: "5px",
//                                   textTransform: "none",
//                                   fontWeight: 700,
//                                   color: style.color4,
//                                   backgroundColor: "rgba(255,255,255,0.05)",
//                                   border: "1px solid rgba(255,255,255,0.10)",
//                                   "&:hover": {
//                                     backgroundColor: "rgba(255,255,255,0.10)",
//                                   },
//                                 }}
//                               >
//                                 Download for Mac
//                               </Button>
//                             )}
//                           </Stack>
//                         </>
//                       )}
//                     </Stack>
//                   </Paper>

//                   {productData.audioURIs?.[0] && (
//                     <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
//                       <Stack spacing={1}>
//                         <Stack direction="row" spacing={1} alignItems="center">
//                           <HeadphonesRoundedIcon sx={{ color: style.color3 }} />
//                           <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
//                             Audio Demo
//                           </Typography>
//                         </Stack>
//                         <audio controls style={{ width: "100%", height: "36px" }}>
//                           <source src={productData.audioURIs[0]} type="audio/mpeg" />
//                         </audio>
//                       </Stack>
//                     </Paper>
//                   )}

//                   <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
//                     <Stack spacing={1.25}>
//                       <Stack direction="row" spacing={1} alignItems="center">
//                         <DownloadRoundedIcon sx={{ color: style.color3 }} />
//                         <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
//                           Download Information
//                         </Typography>
//                       </Stack>

//                       <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
//                         Availability: In stock. This product is delivered as an instant digital download.
//                       </Typography>

//                       {productData.price > 0 && (
//                         <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.25 }}>
//                         <Stack spacing={1}>
//                           <Stack direction="row" spacing={1} alignItems="flex-start">
//                             <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
//                             <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
//                               Download the plugin installer for your device here.
//                             </Typography>
//                           </Stack>

//                           <Stack direction="row" spacing={1} alignItems="flex-start">
//                             <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
//                             <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
//                               To obtain a license, purchase the plugin or start a subscription free trial (can be cancelled any time).
//                             </Typography>
//                           </Stack>

//                           <Stack direction="row" spacing={1} alignItems="flex-start">
//                             <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
//                             <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
//                               After installation, open the plugin in your DAW and sign in with your email address and password to unlock it.
//                             </Typography>
//                           </Stack>
//                         </Stack>
//                       </Paper>
//                       )}

//                       <Stack spacing={1}>
//                         {productData.windowsDownloadURI && (
//                           <Button
//                             component="a"
//                             href={productData.windowsDownloadURI}
//                             startIcon={<WindowsRoundedIcon />}
//                             sx={{
//                               borderRadius: "5px",
//                               textTransform: "none",
//                               fontWeight: 700,
//                               color: style.color4,
//                               backgroundColor: "rgba(255,255,255,0.05)",
//                               border: "1px solid rgba(255,255,255,0.10)",
//                               "&:hover": {
//                                 backgroundColor: "rgba(255,255,255,0.10)",
//                               },
//                             }}
//                           >
//                             Windows Download
//                           </Button>
//                         )}

//                         {productData.macDownloadURI && (
//                           <Button
//                             component="a"
//                             href={productData.macDownloadURI}
//                             startIcon={<AppleIcon />}
//                             sx={{
//                               borderRadius: "5px",
//                               textTransform: "none",
//                               fontWeight: 700,
//                               color: style.color4,
//                               backgroundColor: "rgba(255,255,255,0.05)",
//                               border: "1px solid rgba(255,255,255,0.10)",
//                               "&:hover": {
//                                 backgroundColor: "rgba(255,255,255,0.10)",
//                               },
//                             }}
//                           >
//                             Mac Download
//                           </Button>
//                         )}
//                       </Stack>
//                     </Stack>
//                   </Paper>
//                 </Stack>
//               ) : (
//                 <Box
//                   sx={{
//                     display: "grid",
//                     gridTemplateColumns: "minmax(360px, 0.92fr) minmax(0, 1.08fr)",
//                     gap: 2,
//                     alignItems: "start",
//                   }}
//                 >
//                   <Stack spacing={1.5}>
//                     <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
//                       <Box
//                         sx={{
//                           minHeight: 420,
//                           height: 420,
//                           display: "flex",
//                           alignItems: "center",
//                           justifyContent: "center",
//                           backgroundColor: "rgba(0,0,0,0.18)",
//                           borderRadius: "12px",
//                           overflow: "hidden",
//                           position: "relative",
//                         }}
//                       >
//                         {galleryImages.length > 1 && (
//                           <>
//                             <IconButton
//                               onClick={previousImage}
//                               sx={{
//                                 position: "absolute",
//                                 left: 10,
//                                 zIndex: 2,
//                                 backgroundColor: "rgba(0,0,0,0.32)",
//                                 color: style.color4,
//                                 "&:hover": { backgroundColor: "rgba(0,0,0,0.48)" },
//                               }}
//                             >
//                               <ArrowBackIosNewRoundedIcon fontSize="small" />
//                             </IconButton>

//                             <IconButton
//                               onClick={nextImage}
//                               sx={{
//                                 position: "absolute",
//                                 right: 10,
//                                 zIndex: 2,
//                                 backgroundColor: "rgba(0,0,0,0.32)",
//                                 color: style.color4,
//                                 "&:hover": { backgroundColor: "rgba(0,0,0,0.48)" },
//                               }}
//                             >
//                               <ArrowForwardIosRoundedIcon fontSize="small" />
//                             </IconButton>
//                           </>
//                         )}

//                         <Box
//                           component="img"
//                           src={selectedImage?.URI}
//                           alt={selectedImage?.altText || productData.name}
//                           sx={{
//                             maxWidth: "100%",
//                             maxHeight: "100%",
//                             objectFit: "contain",
//                             display: "block",
//                             p: 1.5,
//                           }}
//                         />
//                       </Box>

//                       {galleryImages.length > 1 && (
//                         <Box
//                           sx={{
//                             mt: 1.25,
//                             display: "grid",
//                             gridTemplateColumns: `repeat(${Math.min(galleryImages.length, 4)}, minmax(0, 1fr))`,
//                             gap: 1,
//                           }}
//                         >
//                           {galleryImages.map((image, index) => (
//                             <Box
//                               key={`${image.URI}-${index}`}
//                               onClick={() => setSelectedImageIndex(index)}
//                               sx={{
//                                 height: 82,
//                                 cursor: "pointer",
//                                 borderRadius: "10px",
//                                 overflow: "hidden",
//                                 border:
//                                   index === selectedImageIndex
//                                     ? `2px solid ${style.color2}`
//                                     : "2px solid transparent",
//                                 backgroundColor:
//                                   index === selectedImageIndex
//                                     ? "rgba(255,255,255,0.06)"
//                                     : "rgba(0,0,0,0.18)",
//                                 "&:hover": { borderColor: style.color3 },
//                               }}
//                             >
//                               <Box
//                                 component="img"
//                                 src={image.URI}
//                                 alt={image.altText || `${productData.name} preview ${index + 1}`}
//                                 sx={{
//                                   width: "100%",
//                                   height: "100%",
//                                   objectFit: "contain",
//                                   display: "block",
//                                   p: 0.75,
//                                 }}
//                               />
//                             </Box>
//                           ))}
//                         </Box>
//                       )}
//                     </Paper>

//                     {productData.audioURIs?.[0] && (
//                       <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
//                         <Stack spacing={1}>
//                           <Stack direction="row" spacing={1} alignItems="center">
//                             <HeadphonesRoundedIcon sx={{ color: style.color3 }} />
//                             <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
//                               Audio Demo
//                             </Typography>
//                           </Stack>
//                           <audio controls style={{ width: "100%", height: "36px" }}>
//                             <source src={productData.audioURIs[0]} type="audio/mpeg" />
//                           </audio>
//                         </Stack>
//                       </Paper>
//                     )}

//                     <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
//                       <Stack spacing={1.25}>
//                         <Stack direction="row" spacing={1} alignItems="center">
//                           <DownloadRoundedIcon sx={{ color: style.color3 }} />
//                           <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
//                             Download Information
//                           </Typography>
//                         </Stack>

//                         <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
//                           Availability: In stock. This product is delivered as an instant digital download.
//                         </Typography>

//                         {productData.price > 0 && (
//                           <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.25 }}>
//                             <Stack spacing={1}>
//                               <Stack direction="row" spacing={1} alignItems="flex-start">
//                                 <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
//                                 <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
//                                   Download the plugin installer for your device here.
//                                 </Typography>
//                               </Stack>

//                               <Stack direction="row" spacing={1} alignItems="flex-start">
//                                 <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
//                                 <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
//                                   To obtain a license, purchase the plugin or start a subscription free trial (can be cancelled any time).
//                                 </Typography>
//                               </Stack>

//                               <Stack direction="row" spacing={1} alignItems="flex-start">
//                                 <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
//                                 <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
//                                   After installation, open the plugin in your DAW and sign in with your email address and password to unlock it.
//                                 </Typography>
//                               </Stack>
//                             </Stack>
//                           </Paper>
//                         )}

//                         <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
//                           {productData.windowsDownloadURI && (
//                             <Button
//                               component="a"
//                               href={productData.windowsDownloadURI}
//                               startIcon={<WindowsRoundedIcon />}
//                               sx={{
//                                 borderRadius: "5px",
//                                 textTransform: "none",
//                                 fontWeight: 700,
//                                 color: style.color4,
//                                 backgroundColor: "rgba(255,255,255,0.05)",
//                                 border: "1px solid rgba(255,255,255,0.10)",
//                                 "&:hover": {
//                                   backgroundColor: "rgba(255,255,255,0.10)",
//                                 },
//                                 flex: 1,
//                               }}
//                             >
//                               Windows Download
//                             </Button>
//                           )}

//                           {productData.macDownloadURI && (
//                             <Button
//                               component="a"
//                               href={productData.macDownloadURI}
//                               startIcon={<AppleIcon />}
//                               sx={{
//                                 borderRadius: "5px",
//                                 textTransform: "none",
//                                 fontWeight: 700,
//                                 color: style.color4,
//                                 backgroundColor: "rgba(255,255,255,0.05)",
//                                 border: "1px solid rgba(255,255,255,0.10)",
//                                 "&:hover": {
//                                   backgroundColor: "rgba(255,255,255,0.10)",
//                                 },
//                                 flex: 1,
//                               }}
//                             >
//                               Mac Download
//                             </Button>
//                           )}
//                         </Stack>
//                       </Stack>
//                     </Paper>
//                   </Stack>

//                   <Stack spacing={1.5}>
//                     <Paper elevation={0} sx={{ ...panelSx, p: 1.75 }}>
//                       <Stack spacing={1}>
//                         <Chip
//                           label={productData.price > 0 ? "Premium Plugin" : "Free Plugin"}
//                           sx={{
//                             alignSelf: "flex-start",
//                             borderRadius: "5px",
//                             color: style.color4,
//                             fontWeight: 700,
//                             backgroundColor:
//                               productData.price > 0
//                                 ? "rgba(255,255,255,0.06)"
//                                 : "rgba(0,180,60,0.18)",
//                             border:
//                               productData.price > 0
//                                 ? "1px solid rgba(255,255,255,0.08)"
//                                 : "1px solid rgba(0,200,80,0.35)",
//                           }}
//                         />

//                         <Typography
//                           variant="h1"
//                           sx={{
//                             fontSize: { xs: "2rem", md: "2.65rem" },
//                             lineHeight: 1.04,
//                             fontWeight: 800,
//                             color: style.color4,
//                             letterSpacing: "-0.03em",
//                           }}
//                         >
//                           {productData.name}
//                         </Typography>

//                         <Typography
//                           sx={{
//                             color: style.color3,
//                             fontSize: { xs: "1rem", md: "1.03rem" },
//                             lineHeight: 1.7,
//                           }}
//                         >
//                           {productData.shortDescription}
//                         </Typography>

//                         <Box
//                           component={RouterLink}
//                           to={`/reviews/${productData.productURI}`}
//                           sx={{
//                             display: "inline-flex",
//                             alignItems: "center",
//                             gap: 1,
//                             flexWrap: "wrap",
//                             width: "fit-content",
//                             textDecoration: "none",
//                             color: "inherit",
//                             pt: 0.25,
//                           }}
//                         >
//                           <Rating value={productData.starRating || 0} precision={0.5} readOnly />
//                           <Typography sx={{ fontWeight: 700, color: style.color4 }}>
//                             {productData.ratingCount > 0 ? productData.starRating : ""}
//                           </Typography>
//                           <Typography sx={{ color: style.color3 }}>
//                             ({productData.ratingCount} review{productData.ratingCount === 1 ? "" : "s"})
//                           </Typography>
//                         </Box>
//                       </Stack>

//                       <Divider sx={{ my: 1.5, backgroundColor: "rgba(255,255,255,0.08)" }} />

//                       <Stack spacing={1.5}>
//                         <Chip
//                           label="IN STOCK • INSTANT DOWNLOAD"
//                           size="small"
//                           sx={{
//                             alignSelf: "flex-start",
//                             color: style.color4,
//                             backgroundColor: "rgba(0,180,60,0.18)",
//                             border: "1px solid rgba(0,200,80,0.35)",
//                             fontWeight: 700,
//                             letterSpacing: 0.2,
//                           }}
//                         />

//                         {productData.price > 0 ? (
//                           <>
//                             <Typography sx={{ color: style.color4, fontWeight: 700 }}>
//                               Purchase this plugin
//                             </Typography>
//                             <Stack spacing={0.8}>
//                                 {hasPromoDiscount && (
//                                   <Chip
//                                     label={`FLASH SALE - Save ${savingsPercent}%`}
//                                     size="small"
//                                     sx={{
//                                       alignSelf: "flex-start",
//                                       color: "#111",
//                                       backgroundColor: "#ffcc4d",
//                                       border: "1px solid rgba(255,255,255,0.18)",
//                                       fontWeight: 900,
//                                       letterSpacing: 0.2,
//                                     }}
//                                   />
//                                 )}

//                                 <Stack direction="row" spacing={1.25} alignItems="baseline" flexWrap="wrap">
//                                   <Typography
//                                     sx={{
//                                       color: style.color3,
//                                       textDecoration: "line-through",
//                                       fontSize: "1.05rem",
//                                     }}
//                                   >
//                                     ${hasPromoDiscount ? originalPrice.toFixed(2) : Number(productData.msrp).toFixed(2)}
//                                   </Typography>

//                                   <Typography
//                                     sx={{
//                                       fontSize: "2rem",
//                                       fontWeight: 900,
//                                       color: style.color4,
//                                       lineHeight: 1,
//                                     }}
//                                   >
//                                     ${finalPrice.toFixed(2)}
//                                   </Typography>
//                                 </Stack>

//                                 {hasPromoDiscount && (
//                                   <Typography sx={{ color: style.color3, fontSize: "0.92rem", lineHeight: 1.45 }}>
//                                     Sale price applied automatically at checkout.
//                                   </Typography>
//                                 )}
//                               </Stack>

//                             {/* <Stack direction="row" spacing={1.5} alignItems="baseline" flexWrap="wrap">
//                               <Typography
//                                 sx={{
//                                   color: style.color3,
//                                   textDecoration: "line-through",
//                                   fontSize: "1.15rem",
//                                 }}
//                               >
//                                 ${Number(productData.msrp).toFixed(2)}
//                               </Typography>
//                               <Typography
//                                 sx={{
//                                   fontSize: "2.1rem",
//                                   fontWeight: 800,
//                                   color: style.color4,
//                                   lineHeight: 1,
//                                 }}
//                               >
//                                 ${Number(productData.price).toFixed(2)}
//                               </Typography>
//                             </Stack> */}

//                             <Typography sx={{ color: style.color3, lineHeight: 1.65 }}>
//                               One-time purchase. Add this plugin to your cart and own it permanently.
//                             </Typography>

//                             <Button
//                               onClick={addProductToCart}
//                               startIcon={<ShoppingCartRoundedIcon />}
//                               sx={{
//                                 alignSelf: "flex-start",
//                                 borderRadius: "5px",
//                                 textTransform: "none",
//                                 fontWeight: 700,
//                                 color: style.color4,
//                                 backgroundColor: style.accentGreen,
//                                 px: 2.5,
//                                 "&:hover": {
//                                   backgroundColor: style.accentGreen,
//                                   filter: "brightness(1.05)",
//                                 },
//                               }}
//                             >
//                               Add to Cart
//                             </Button>

//                             <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

//                             <Stack spacing={0.8}>
//                               <Stack direction="row" spacing={1} alignItems="center">
//                                 <AutoAwesomeRoundedIcon sx={{ color: style.color3 }} />
//                                 <Typography sx={{ color: style.color4, fontWeight: 700 }}>
//                                   Subscription
//                                 </Typography>
//                               </Stack>

//                               <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
//                                 Try it free for 7 days with an all access subscription.
//                               </Typography>

//                               <Button
//                                 href='/subscribe'
//                                 startIcon={<BoltRoundedIcon />}
//                                 sx={{
//                                   alignSelf: "flex-start",
//                                   borderRadius: "5px",
//                                   textTransform: "none",
//                                   fontWeight: 700,
//                                   color: style.color4,
//                                   backgroundColor: "rgba(255,255,255,0.05)",
//                                   border: "1px solid rgba(255,255,255,0.10)",
//                                   px: 2.5,
//                                   "&:hover": {
//                                     backgroundColor: "rgba(255,255,255,0.10)",
//                                   },
//                                 }}
//                               >
//                                 Try FREE for 7 Days
//                               </Button>

//                               <Typography sx={{ color: style.color3, fontSize: "0.92rem" }}>
//                                 $9.95 per month for access to all plugins. Cancel anytime.
//                               </Typography>
//                             </Stack>
//                           </>
//                         ) : (
//                           <>
//                             <Typography
//                               sx={{
//                                 fontWeight: 900,
//                                 fontSize: "2.2rem",
//                                 color: "rgb(30,170,60)",
//                                 lineHeight: 1,
//                               }}
//                             >
//                               FREE
//                             </Typography>

//                             <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
//                               Download this VST3 plugin for Mac or Windows.
//                             </Typography>

//                             <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
//                               {productData.windowsDownloadURI && (
//                                 <Button
//                                   onClick={downloadWindowsVST}
//                                   startIcon={<WindowsRoundedIcon />}
//                                   sx={{
//                                     borderRadius: "5px",
//                                     textTransform: "none",
//                                     fontWeight: 700,
//                                     color: style.color4,
//                                     backgroundColor: style.accentGreen,
//                                     "&:hover": {
//                                       backgroundColor: style.accentGreen,
//                                       filter: "brightness(1.05)",
//                                     },
//                                   }}
//                                 >
//                                   Download for Windows
//                                 </Button>
//                               )}

//                               {productData.macDownloadURI && (
//                                 <Button
//                                   onClick={downloadMacVST}
//                                   startIcon={<AppleIcon />}
//                                   sx={{
//                                     borderRadius: "5px",
//                                     textTransform: "none",
//                                     fontWeight: 700,
//                                     color: style.color4,
//                                     backgroundColor: "rgba(255,255,255,0.05)",
//                                     border: "1px solid rgba(255,255,255,0.10)",
//                                     "&:hover": {
//                                       backgroundColor: "rgba(255,255,255,0.10)",
//                                     },
//                                   }}
//                                 >
//                                   Download for Mac
//                                 </Button>
//                               )}
//                             </Stack>
//                           </>
//                         )}
//                       </Stack>
//                     </Paper>
//                   </Stack>
//                 </Box>
//               )}

//               <Box sx={{ mt: 1.5 }}>
//                 <Box
//                   sx={{
//                     display: "grid",
//                     gridTemplateColumns: { xs: "1fr", md: "1.2fr 0.8fr" },
//                     gap: 2,
//                     alignItems: "stretch",
//                   }}
//                 >
//                   <Paper elevation={0} sx={{ ...panelSx, p: { xs: 1.5, md: 2 } }}>
//                     <Stack spacing={1.5}>
//                       <Stack direction="row" spacing={1} alignItems="center">
//                         <LibraryMusicRoundedIcon sx={{ color: style.color3 }} />
//                         <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: style.color4 }}>
//                           Overview
//                         </Typography>
//                       </Stack>

//                       <Typography
//                         sx={{
//                           color: style.color4,
//                           lineHeight: 1.75,
//                           whiteSpace: "pre-line",
//                           fontSize: "1rem",
//                         }}
//                       >
//                         {productData.longDescription}
//                       </Typography>

//                       {bulletPoints.length > 0 && (
//                         <>
//                           <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

//                           <Stack spacing={1}>
//                             <Stack direction="row" spacing={1} alignItems="center">
//                               <InfoOutlinedIcon sx={{ color: style.color3 }} />
//                               <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
//                                 Highlights
//                               </Typography>
//                             </Stack>

//                             <Box
//                               sx={{
//                                 display: "grid",
//                                 gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
//                                 gap: 1.1,
//                               }}
//                             >
//                               {bulletPoints.map((point, idx) => (
//                                 <Paper
//                                   key={idx}
//                                   elevation={0}
//                                   sx={{
//                                     ...subtlePanelSx,
//                                     p: 1.15,
//                                     minHeight: 25,
//                                     display: "flex",
//                                     alignItems: "flex-start",
//                                   }}
//                                 >
//                                   <Stack direction="row" spacing={1} alignItems="flex-start">
//                                     <CheckCircleRoundedIcon
//                                       sx={{
//                                         color: style.color3,
//                                         mt: "2px",
//                                         fontSize: "1.05rem",
//                                         flexShrink: 0,
//                                       }}
//                                     />
//                                     <Typography
//                                       sx={{
//                                         color: style.color4,
//                                         lineHeight: 1.5,
//                                         fontSize: "0.92rem",
//                                         wordBreak: "break-word",
//                                       }}
//                                     >
//                                       {point}
//                                     </Typography>
//                                   </Stack>
//                                 </Paper>
//                               ))}
//                             </Box>
//                           </Stack>
//                         </>
//                       )}
//                     </Stack>
//                   </Paper>

//                   <Paper elevation={0} sx={{ ...panelSx, p: { xs: 1.5, md: 2 } }}>
//                     <Stack spacing={1.5}>
//                       <Stack direction="row" spacing={1} alignItems="center">
//                         <TuneRoundedIcon sx={{ color: style.color3 }} />
//                         <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: style.color4 }}>
//                           Controls
//                         </Typography>
//                       </Stack>

//                       {productData.controls?.length > 0 ? (
//                         <Stack spacing={1.1}>
//                           {productData.controls.map((control, index) => (
//                             <Card
//                               key={index}
//                               elevation={0}
//                               sx={{
//                                 backgroundColor: "rgba(0,0,0,0.18)",
//                                 color: style.color4,
//                                 borderRadius: "12px",
//                                 border: "1px solid rgba(255,255,255,0.06)",
//                                 boxShadow: "none",
//                               }}
//                             >
//                               <CardContent sx={{ p: "14px !important" }}>
//                                 <Typography
//                                   sx={{
//                                     fontWeight: 800,
//                                     color: style.color4,
//                                     mb: 0.4,
//                                   }}
//                                 >
//                                   {control.name}
//                                 </Typography>
//                                 <Typography
//                                   sx={{
//                                     color: style.color3,
//                                     lineHeight: 1.55,
//                                     fontSize: "0.92rem",
//                                   }}
//                                 >
//                                   {control.description}
//                                 </Typography>
//                               </CardContent>
//                             </Card>
//                           ))}
//                         </Stack>
//                       ) : (
//                         <Typography sx={{ color: style.color3 }}>
//                           No control information available.
//                         </Typography>
//                       )}
//                     </Stack>
//                   </Paper>
//                 </Box>
//               </Box>

//               <Divider sx={{ my: 2, backgroundColor: "rgba(255,255,255,0.08)" }} />

//               <Stack
//                 direction={{ xs: "column", md: "row" }}
//                 spacing={1.5}
//                 justifyContent="space-between"
//                 alignItems={{ xs: "flex-start", md: "center" }}
//               >
//                 <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
//                   <Typography sx={{ color: style.color3, fontSize: "0.92rem" }}>
//                     Format:
//                   </Typography>
//                   <Chip
//                     label="VST3"
//                     size="small"
//                     sx={{
//                       backgroundColor: "rgba(255,255,255,0.08)",
//                       color: style.color4,
//                     }}
//                   />
//                   <Chip
//                     label="Mac"
//                     size="small"
//                     sx={{
//                       backgroundColor: "rgba(255,255,255,0.08)",
//                       color: style.color4,
//                     }}
//                   />
//                   <Chip
//                     label="Windows"
//                     size="small"
//                     sx={{
//                       backgroundColor: "rgba(255,255,255,0.08)",
//                       color: style.color4,
//                     }}
//                   />
//                 </Stack>

//                 <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
//                   <Button
//                     component={RouterLink}
//                     to={`/reviews/${productData.productURI}`}
//                     variant="text"
//                     sx={{
//                       color: style.color3,
//                       fontWeight: 700,
//                       px: 0,
//                       "&:hover": {
//                         backgroundColor: "transparent",
//                         color: style.color4,
//                       },
//                     }}
//                   >
//                     Read all reviews
//                   </Button>

//                   <Button
//                     component={RouterLink}
//                     to="/plugins"
//                     variant="text"
//                     sx={{
//                       color: style.color3,
//                       fontWeight: 700,
//                       px: 0,
//                       "&:hover": {
//                         backgroundColor: "transparent",
//                         color: style.color4,
//                       },
//                     }}
//                   >
//                     Browse all plugins
//                   </Button>
//                 </Stack>
//               </Stack>
//             </Box>
//           </Paper>
//         </Box>
//       </Box>

//       <LoginRegisterModal
//         isOpen={isLoginRegisterModalOpen}
//         close={closeModal(setLoginRegisterModalOpen)}
//         loginComplete={loginCallback}
//       />
//     </>
//   );
// }

// import React, { useState, useContext, useEffect, useMemo } from "react";
// import axios from "axios";
// import { Link as RouterLink, useParams, useNavigate } from "react-router-dom";
// import { UserContext } from "../../context/userContext/userContext";
// import { useStyle } from "../../context/styleContext/styleContext";
// import { ShoppingCartContext } from "../../context/shoppingCartContext/shoppingCartContext";
// import { toast } from "react-hot-toast";
// import ReactGA from "react-ga4";
// import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
// import Seo from "../../components/Seo";
// import LoginRegisterModal from "./LoginRegisterModal";
// import ProductSchemaMarkup from "./ProductSchemaMarkup";
// import { usePromotions } from "../../context/promotionsContext/promotionsContext";
// import {
//   Box,
//   Typography,
//   Button,
//   useMediaQuery,
//   Rating,
//   Divider,
//   Stack,
//   Paper,
//   Chip,
//   IconButton,
//   Card,
//   CardContent,
// } from "@mui/material";
// import { useTheme } from "@mui/material/styles";
// import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
// import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
// import WindowsRoundedIcon from "@mui/icons-material/DesktopWindowsRounded";
// import AppleIcon from "@mui/icons-material/Apple";
// import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
// import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
// import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
// import HeadphonesRoundedIcon from "@mui/icons-material/HeadphonesRounded";
// import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
// import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
// import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
// import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
// import CheckCircleIcon from "@mui/icons-material/CheckCircle";
// import BoltRoundedIcon from "@mui/icons-material/BoltRounded";

// export default function ProductPage() {
//   const { user } = useContext(UserContext);
//   const { style } = useStyle();
//   const { addToCart } = useContext(ShoppingCartContext);
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("md"));
//   const { sku } = useParams();
//   const navigate = useNavigate();

//   const [productData, setProductData] = useState(null);
//   const [windowsDownloadRequested, setWindowsDownloadRequested] = useState(false);
//   const [macDownloadRequested, setMacDownloadRequested] = useState(false);
//   const [isLoginRegisterModalOpen, setLoginRegisterModalOpen] = useState(false);
//   const [height, setHeight] = useState(window.innerHeight);
//   const [selectedImageIndex, setSelectedImageIndex] = useState(0);
//   const { getProductDisplayPricing } = usePromotions();
//   const productPricing = useMemo(() => {
//     if (!productData || Number(productData.price) <= 0) return null;
//     return getProductDisplayPricing(productData);
//   }, [productData, getProductDisplayPricing]);
  
//   const hasPromoDiscount = Boolean(productPricing?.hasDiscount);
//   const originalPrice = Number(productPricing?.originalPrice ?? productData?.price ?? 0);
//   const finalPrice = Number(productPricing?.finalPrice ?? productData?.price ?? 0);
//   const savingsPercent =
//     hasPromoDiscount && originalPrice > 0
//       ? Math.round((Number(productPricing.discountAmount) / originalPrice) * 100)
//       : 0;


//   useEffect(() => {
//     ReactGA.send({ hitType: "pageview", page: sku, title: sku });
//     getProductPageInfo();
//   }, [sku]);

//   useEffect(() => {
//     function handleResize() {
//       setHeight(window.innerHeight);
//     }
//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   useEffect(() => {
//     setSelectedImageIndex(0);
//   }, [productData?.productURI]);

//   useEffect(() => {
//     window.scrollTo({ top: 0, left: 0, behavior: "auto" });
//   }, [sku]);

//   const bulletPoints = useMemo(() => {
//     if (!productData) return [];
//     return [
//       productData.bulletPoint1,
//       productData.bulletPoint2,
//       productData.bulletPoint3,
//       productData.bulletPoint4,
//       productData.bulletPoint5,
//     ].filter(Boolean);
//   }, [productData]);

//   const galleryImages = useMemo(() => {
//     if (!productData?.images) return [];
//     const valid = productData.images.filter((img) => img && img.URI);
//     if (valid.length <= 1) return valid;
//     return valid.slice(0, -1);
//   }, [productData]);

//   const selectedImage = galleryImages[selectedImageIndex] || galleryImages[0];

//   const closeModal = (setModalFn) => () => setModalFn(false);

//   const getProductPageInfo = async () => {
//     try {
//       const { data } = await axios.post("/get_product_page_info", { productURI: sku });
//       if (data) {
//         setProductData(data);
//       }
//     } catch (error) {
//       console.log("Error retrieving data...", error);
//     }
//   };

//   const addProductToCart = (e) => {
//     e.preventDefault();
//     addToCart(productData.SKU);
//     //toast.success(`${productData.name} added to cart`);
//   };

//   const downloadWindowsVST = async () => {
//     const token = localStorage.getItem("token");

//     if (!user) {
//       setWindowsDownloadRequested(true);
//       setLoginRegisterModalOpen(true);
//       return;
//     }

//     try {
//       await axios.post("/api/register_free_product", {
//         token,
//         sku: productData.SKU,
//         productVersion: productData.productVersion,
//       });

//       window.location.href = productData.windowsDownloadURI;
//     } catch (error) {
//       toast.error("Error: your request could not be completed at this time.");
//     }
//   };

//   const downloadMacVST = async () => {
//     const token = localStorage.getItem("token");

//     if (!user) {
//       setMacDownloadRequested(true);
//       setLoginRegisterModalOpen(true);
//       return;
//     }

//     try {
//       await axios.post("/api/register_free_product", {
//         token,
//         sku: productData.SKU,
//         productVersion: productData.productVersion,
//       });

//       window.location.href = productData.macDownloadURI;
//     } catch (error) {
//       toast.error("Error: your request could not be completed at this time.");
//     }
//   };

//   const navToSubscribePage = () => {
//     navigate("/subscribe");
//   };

//   const loginCallback = async () => {
//     const token = localStorage.getItem("token");
//     if (!token || !productData) return;

//     setLoginRegisterModalOpen(false);

//     try {
//       if (windowsDownloadRequested) {
//         setWindowsDownloadRequested(false);
//         await axios.post("/api/register_free_product", {
//           token,
//           sku: productData.SKU,
//           productVersion: productData.productVersion,
//         });
//         window.location.href = productData.windowsDownloadURI;
//       }

//       if (macDownloadRequested) {
//         setMacDownloadRequested(false);
//         await axios.post("/api/register_free_product", {
//           token,
//           sku: productData.SKU,
//           productVersion: productData.productVersion,
//         });
//         window.location.href = productData.macDownloadURI;
//       }
//     } catch (error) {
//       toast.error("Error: your request could not be completed at this time.");
//     }
//   };

//   const nextImage = () => {
//     if (galleryImages.length < 2) return;
//     setSelectedImageIndex((prev) => (prev + 1) % galleryImages.length);
//   };

//   const previousImage = () => {
//     if (galleryImages.length < 2) return;
//     setSelectedImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
//   };

//   if (!productData) {
//     return (
//       <>
//         <Seo
//           title="Parish Audio Plugin"
//           description="Professional audio plugins by Parish Audio."
//           canonical="https://parishaudio.com/products"
//         />
//         <Box sx={{ minHeight: height - 130 }}>
//           <LoadingSpinner />
//         </Box>
//       </>
//     );
//   }

//   const panelSx = {
//     borderRadius: "16px",
//     backgroundColor: style.darkPanel,
//     border: "1px solid rgba(255,255,255,0.08)",
//     boxShadow: "none",
//   };

//   const subtlePanelSx = {
//     borderRadius: "14px",
//     backgroundColor: style.darkerPanel,
//     border: "1px solid rgba(255,255,255,0.06)",
//     boxShadow: "none",
//   };

//   return (
//     <>
//       <Seo
//         title={`${productData.name} VST3 Plugin | Parish Audio`}
//         description={`${productData.name} by Parish Audio. ${productData.shortDescription} Available for Mac and Windows as a VST3 plugin.`}
//         canonical={`https://parishaudio.com/products/${productData.productURI}`}
//         image={
//           productData?.images?.[0]?.URI
//             ? `https://parishaudio.com${productData.images[0].URI}`
//             : "https://parishaudio.com/assets/og-default.jpg"
//         }
//         imageAlt={
//           productData?.images?.[0]?.altText || productData?.name || "Parish Audio plugin"
//         }
//         ogType="product"
//       />

// <ProductSchemaMarkup
//   productData={productData}
//   productPricing={productPricing}
// />

//       <Box
//         sx={{
//           width: "100%",
//           maxWidth: "100vw",
//           overflowX: "hidden",
//           backgroundColor: "black",
//           px: { xs: 1.25, sm: 1.5, md: 2.5 },
//           py: { xs: 1.5, md: 2.5 },
//           boxSizing: "border-box",
//         }}
//       >
//         <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
//           <Paper
//             elevation={0}
//             sx={{
//               width: "100%",
//               minWidth: 0,
//               borderRadius: "20px",
//               overflow: "hidden",
//               backgroundColor: style.color1,
//               border: `1px solid ${style.color2}`,
//               boxSizing: "border-box",
//             }}
//           >
//             <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
//               {isMobile ? (
//                 <Stack spacing={1.5}>
//                   <Paper elevation={0} sx={{ ...panelSx, p: 1.25 }}>
//                     <Stack spacing={1}>
//                       <Chip
//                         label={productData.price > 0 ? "Premium Plugin" : "Free Plugin"}
//                         sx={{
//                           alignSelf: "flex-start",
//                           borderRadius: "5px",
//                           color: style.color4,
//                           fontWeight: 700,
//                           backgroundColor:
//                             productData.price > 0
//                               ? "rgba(255,255,255,0.06)"
//                               : "rgba(0,180,60,0.18)",
//                           border:
//                             productData.price > 0
//                               ? "1px solid rgba(255,255,255,0.08)"
//                               : "1px solid rgba(0,200,80,0.35)",
//                         }}
//                       />

//                       <Typography
//                         variant="h1"
//                         sx={{
//                           fontSize: { xs: "2rem", md: "2.7rem" },
//                           lineHeight: 1.04,
//                           fontWeight: 800,
//                           color: style.color4,
//                           letterSpacing: "-0.03em",
//                         }}
//                       >
//                         {productData.name}
//                       </Typography>

//                       <Typography
//                         sx={{
//                           color: style.color3,
//                           fontSize: "1rem",
//                           lineHeight: 1.7,
//                         }}
//                       >
//                         {productData.shortDescription}
//                       </Typography>

//                       <Box
//                         component={RouterLink}
//                         to={`/reviews/${productData.productURI}`}
//                         sx={{
//                           display: "inline-flex",
//                           alignItems: "center",
//                           gap: 1,
//                           flexWrap: "wrap",
//                           width: "fit-content",
//                           textDecoration: "none",
//                           color: "inherit",
//                         }}
//                       >
//                         <Rating value={productData.starRating || 0} precision={0.5} readOnly />
//                         <Typography sx={{ fontWeight: 700, color: style.color4 }}>
//                           {productData.ratingCount > 0 ? productData.starRating : ""}
//                         </Typography>
//                         <Typography sx={{ color: style.color3 }}>
//                           ({productData.ratingCount} review{productData.ratingCount === 1 ? "" : "s"})
//                         </Typography>
//                       </Box>
//                     </Stack>
//                   </Paper>

//                   <Paper elevation={0} sx={{ ...panelSx, p: 1.25 }}>
//                     <Box
//                       sx={{
//                         minHeight: 260,
//                         height: 260,
//                         display: "flex",
//                         alignItems: "center",
//                         justifyContent: "center",
//                         backgroundColor: "rgba(0,0,0,0.18)",
//                         borderRadius: "12px",
//                         overflow: "hidden",
//                         position: "relative",
//                       }}
//                     >
//                       {galleryImages.length > 1 && (
//                         <>
//                           <IconButton
//                             onClick={previousImage}
//                             sx={{
//                               position: "absolute",
//                               left: 8,
//                               zIndex: 2,
//                               backgroundColor: "rgba(0,0,0,0.32)",
//                               color: style.color4,
//                               "&:hover": { backgroundColor: "rgba(0,0,0,0.48)" },
//                             }}
//                           >
//                             <ArrowBackIosNewRoundedIcon fontSize="small" />
//                           </IconButton>

//                           <IconButton
//                             onClick={nextImage}
//                             sx={{
//                               position: "absolute",
//                               right: 8,
//                               zIndex: 2,
//                               backgroundColor: "rgba(0,0,0,0.32)",
//                               color: style.color4,
//                               "&:hover": { backgroundColor: "rgba(0,0,0,0.48)" },
//                             }}
//                           >
//                             <ArrowForwardIosRoundedIcon fontSize="small" />
//                           </IconButton>
//                         </>
//                       )}

//                       <Box
//                         component="img"
//                         src={selectedImage?.URI}
//                         alt={selectedImage?.altText || productData.name}
//                         sx={{
//                           maxWidth: "100%",
//                           maxHeight: "100%",
//                           objectFit: "contain",
//                           display: "block",
//                           p: 1,
//                         }}
//                       />
//                     </Box>

//                     {galleryImages.length > 1 && (
//                       <Box
//                         sx={{
//                           mt: 1,
//                           display: "grid",
//                           gridTemplateColumns: `repeat(${Math.min(galleryImages.length, 4)}, minmax(0, 1fr))`,
//                           gap: 1,
//                         }}
//                       >
//                         {galleryImages.map((image, index) => (
//                           <Box
//                             key={`${image.URI}-${index}`}
//                             onClick={() => setSelectedImageIndex(index)}
//                             sx={{
//                               height: 74,
//                               cursor: "pointer",
//                               borderRadius: "10px",
//                               overflow: "hidden",
//                               border:
//                                 index === selectedImageIndex
//                                   ? `2px solid ${style.color2}`
//                                   : "2px solid transparent",
//                               backgroundColor:
//                                 index === selectedImageIndex
//                                   ? "rgba(255,255,255,0.06)"
//                                   : "rgba(0,0,0,0.18)",
//                               "&:hover": { borderColor: style.color3 },
//                             }}
//                           >
//                             <Box
//                               component="img"
//                               src={image.URI}
//                               alt={image.altText || `${productData.name} preview ${index + 1}`}
//                               sx={{
//                                 width: "100%",
//                                 height: "100%",
//                                 objectFit: "contain",
//                                 display: "block",
//                                 p: 0.75,
//                               }}
//                             />
//                           </Box>
//                         ))}
//                       </Box>
//                     )}
//                   </Paper>

//                   <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
//                     <Stack spacing={1.5}>
//                       <Chip
//                         label="IN STOCK • INSTANT DOWNLOAD"
//                         size="small"
//                         sx={{
//                           alignSelf: "flex-start",
//                           color: style.color4,
//                           backgroundColor: "rgba(0,180,60,0.18)",
//                           border: "1px solid rgba(0,200,80,0.35)",
//                           fontWeight: 700,
//                           letterSpacing: 0.2,
//                         }}
//                       />

//                       {productData.price > 0 ? (
//                         <>
//                           <Typography sx={{ color: style.color3, fontWeight: 700 }}>
//                             Purchase this plugin
//                           </Typography>

//                           {/* <Stack direction="row" spacing={1.25} alignItems="baseline" flexWrap="wrap">
//                             <Typography
//                               sx={{
//                                 color: style.color3,
//                                 textDecoration: "line-through",
//                                 fontSize: "1.05rem",
//                               }}
//                             >
//                               ${Number(productData.msrp).toFixed(2)}
//                             </Typography>
//                             <Typography
//                               sx={{
//                                 fontSize: "2rem",
//                                 fontWeight: 800,
//                                 color: style.color4,
//                                 lineHeight: 1,
//                               }}
//                             >
//                               ${Number(productData.price).toFixed(2)}
//                             </Typography>
//                           </Stack> */}

//                       <Stack spacing={0.8}>
//                         {hasPromoDiscount && (
//                           <Chip
//                             label={`FLASH SALE - Save ${savingsPercent}%`}
//                             size="small"
//                             sx={{
//                               alignSelf: "flex-start",
//                               color: "#111",
//                               backgroundColor: "#ffcc4d",
//                               border: "1px solid rgba(255,255,255,0.18)",
//                               fontWeight: 900,
//                               letterSpacing: 0.2,
//                             }}
//                           />
//                         )}

//                         <Stack direction="row" spacing={1.25} alignItems="baseline" flexWrap="wrap">
//                           <Typography
//                             sx={{
//                               color: style.color3,
//                               textDecoration: "line-through",
//                               fontSize: "1.05rem",
//                             }}
//                           >
//                             ${hasPromoDiscount ? originalPrice.toFixed(2) : Number(productData.msrp).toFixed(2)}
//                           </Typography>

//                           {/* {hasPromoDiscount && Number(productData.msrp) > originalPrice && (
//                             <Typography
//                               sx={{
//                                 color: "rgba(255,255,255,0.42)",
//                                 textDecoration: "line-through",
//                                 fontSize: "0.95rem",
//                               }}
//                             >
//                               ${Number(productData.msrp).toFixed(2)}
//                             </Typography>
//                           )} */}

//                           <Typography
//                             sx={{
//                               fontSize: "2rem",
//                               fontWeight: 900,
//                               color: style.color4,
//                               lineHeight: 1,
//                             }}
//                           >
//                             ${finalPrice.toFixed(2)}
//                           </Typography>
//                         </Stack>

//                         {hasPromoDiscount && (
//                           <Typography sx={{ color: style.color3, fontSize: "0.92rem", lineHeight: 1.45 }}>
//                             Sale price applied automatically at checkout.
//                           </Typography>
//                         )}
//                       </Stack>


//                           <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
//                             One-time purchase. Add this plugin to your cart and own it permanently.
//                           </Typography>

//                           <Button
//                             onClick={addProductToCart}
//                             startIcon={<ShoppingCartRoundedIcon />}
//                             sx={{
//                               borderRadius: "5px",
//                               textTransform: "none",
//                               fontWeight: 700,
//                               color: style.color4,
//                               backgroundColor: style.accentGreen,
//                               "&:hover": {
//                                 backgroundColor: style.accentGreen,
//                                 filter: "brightness(1.05)",
//                               },
//                             }}
//                           >
//                             Add to Cart
//                           </Button>

//                           <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

//                           <Stack spacing={0.75}>
//                             <Stack direction="row" spacing={1} alignItems="center">
//                               <AutoAwesomeRoundedIcon sx={{ color: style.color3 }} />
//                               <Typography sx={{ color: style.color4, fontWeight: 700 }}>
//                                 Subscription
//                               </Typography>
//                             </Stack>

//                             <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
//                               Try it free for 7 days with an all access subscription.
//                             </Typography>

//                             <Button
//                               href='/subscribe'
//                               startIcon={<BoltRoundedIcon />}
//                               sx={{
//                                 borderRadius: "5px",
//                                 textTransform: "none",
//                                 fontWeight: 700,
//                                 color: style.color4,
//                                 backgroundColor: "rgba(255,255,255,0.05)",
//                                 border: "1px solid rgba(255,255,255,0.10)",
//                                 "&:hover": {
//                                   backgroundColor: "rgba(255,255,255,0.10)",
//                                 },
//                               }}
//                             >
//                               Try FREE for 7 Days
//                             </Button>
//                           </Stack>
//                         </>
//                       ) : (
//                         <>
//                           <Typography
//                             sx={{
//                               fontWeight: 900,
//                               fontSize: "2rem",
//                               color: "rgb(30,170,60)",
//                               lineHeight: 1,
//                             }}
//                           >
//                             FREE
//                           </Typography>

//                           <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
//                             Download this VST3 plugin for Mac or Windows.
//                           </Typography>

//                           <Stack spacing={1}>
//                             {productData.windowsDownloadURI && (
//                               <Button
//                                 onClick={downloadWindowsVST}
//                                 startIcon={<WindowsRoundedIcon />}
//                                 sx={{
//                                   borderRadius: "5px",
//                                   textTransform: "none",
//                                   fontWeight: 700,
//                                   color: style.color4,
//                                   backgroundColor: style.accentGreen,
//                                   "&:hover": {
//                                     backgroundColor: style.accentGreen,
//                                     filter: "brightness(1.05)",
//                                   },
//                                 }}
//                               >
//                                 Download for Windows
//                               </Button>
//                             )}

//                             {productData.macDownloadURI && (
//                               <Button
//                                 onClick={downloadMacVST}
//                                 startIcon={<AppleIcon />}
//                                 sx={{
//                                   borderRadius: "5px",
//                                   textTransform: "none",
//                                   fontWeight: 700,
//                                   color: style.color4,
//                                   backgroundColor: "rgba(255,255,255,0.05)",
//                                   border: "1px solid rgba(255,255,255,0.10)",
//                                   "&:hover": {
//                                     backgroundColor: "rgba(255,255,255,0.10)",
//                                   },
//                                 }}
//                               >
//                                 Download for Mac
//                               </Button>
//                             )}
//                           </Stack>
//                         </>
//                       )}
//                     </Stack>
//                   </Paper>

//                   {productData.audioURIs?.[0] && (
//                     <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
//                       <Stack spacing={1}>
//                         <Stack direction="row" spacing={1} alignItems="center">
//                           <HeadphonesRoundedIcon sx={{ color: style.color3 }} />
//                           <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
//                             Audio Demo
//                           </Typography>
//                         </Stack>
//                         <audio controls style={{ width: "100%", height: "36px" }}>
//                           <source src={productData.audioURIs[0]} type="audio/mpeg" />
//                         </audio>
//                       </Stack>
//                     </Paper>
//                   )}

//                   <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
//                     <Stack spacing={1.25}>
//                       <Stack direction="row" spacing={1} alignItems="center">
//                         <DownloadRoundedIcon sx={{ color: style.color3 }} />
//                         <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
//                           Download Information
//                         </Typography>
//                       </Stack>

//                       <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
//                         Availability: In stock. This product is delivered as an instant digital download.
//                       </Typography>

//                       {productData.price > 0 && (
//                         <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.25 }}>
//                         <Stack spacing={1}>
//                           <Stack direction="row" spacing={1} alignItems="flex-start">
//                             <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
//                             <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
//                               Download the plugin installer for your device here.
//                             </Typography>
//                           </Stack>

//                           <Stack direction="row" spacing={1} alignItems="flex-start">
//                             <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
//                             <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
//                               To obtain a license, purchase the plugin or start a subscription free trial (can be cancelled any time).
//                             </Typography>
//                           </Stack>

//                           <Stack direction="row" spacing={1} alignItems="flex-start">
//                             <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
//                             <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
//                               After installation, open the plugin in your DAW and sign in with your email address and password to unlock it.
//                             </Typography>
//                           </Stack>
//                         </Stack>
//                       </Paper>
//                       )}

//                       <Stack spacing={1}>
//                         {productData.windowsDownloadURI && (
//                           <Button
//                             component="a"
//                             href={productData.windowsDownloadURI}
//                             startIcon={<WindowsRoundedIcon />}
//                             sx={{
//                               borderRadius: "5px",
//                               textTransform: "none",
//                               fontWeight: 700,
//                               color: style.color4,
//                               backgroundColor: "rgba(255,255,255,0.05)",
//                               border: "1px solid rgba(255,255,255,0.10)",
//                               "&:hover": {
//                                 backgroundColor: "rgba(255,255,255,0.10)",
//                               },
//                             }}
//                           >
//                             Windows Download
//                           </Button>
//                         )}

//                         {productData.macDownloadURI && (
//                           <Button
//                             component="a"
//                             href={productData.macDownloadURI}
//                             startIcon={<AppleIcon />}
//                             sx={{
//                               borderRadius: "5px",
//                               textTransform: "none",
//                               fontWeight: 700,
//                               color: style.color4,
//                               backgroundColor: "rgba(255,255,255,0.05)",
//                               border: "1px solid rgba(255,255,255,0.10)",
//                               "&:hover": {
//                                 backgroundColor: "rgba(255,255,255,0.10)",
//                               },
//                             }}
//                           >
//                             Mac Download
//                           </Button>
//                         )}
//                       </Stack>
//                     </Stack>
//                   </Paper>
//                 </Stack>
//               ) : (
//                 <Box
//                   sx={{
//                     display: "grid",
//                     gridTemplateColumns: "minmax(360px, 0.92fr) minmax(0, 1.08fr)",
//                     gap: 2,
//                     alignItems: "start",
//                   }}
//                 >
//                   <Stack spacing={1.5}>
//                     <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
//                       <Box
//                         sx={{
//                           minHeight: 420,
//                           height: 420,
//                           display: "flex",
//                           alignItems: "center",
//                           justifyContent: "center",
//                           backgroundColor: "rgba(0,0,0,0.18)",
//                           borderRadius: "12px",
//                           overflow: "hidden",
//                           position: "relative",
//                         }}
//                       >
//                         {galleryImages.length > 1 && (
//                           <>
//                             <IconButton
//                               onClick={previousImage}
//                               sx={{
//                                 position: "absolute",
//                                 left: 10,
//                                 zIndex: 2,
//                                 backgroundColor: "rgba(0,0,0,0.32)",
//                                 color: style.color4,
//                                 "&:hover": { backgroundColor: "rgba(0,0,0,0.48)" },
//                               }}
//                             >
//                               <ArrowBackIosNewRoundedIcon fontSize="small" />
//                             </IconButton>

//                             <IconButton
//                               onClick={nextImage}
//                               sx={{
//                                 position: "absolute",
//                                 right: 10,
//                                 zIndex: 2,
//                                 backgroundColor: "rgba(0,0,0,0.32)",
//                                 color: style.color4,
//                                 "&:hover": { backgroundColor: "rgba(0,0,0,0.48)" },
//                               }}
//                             >
//                               <ArrowForwardIosRoundedIcon fontSize="small" />
//                             </IconButton>
//                           </>
//                         )}

//                         <Box
//                           component="img"
//                           src={selectedImage?.URI}
//                           alt={selectedImage?.altText || productData.name}
//                           sx={{
//                             maxWidth: "100%",
//                             maxHeight: "100%",
//                             objectFit: "contain",
//                             display: "block",
//                             p: 1.5,
//                           }}
//                         />
//                       </Box>

//                       {galleryImages.length > 1 && (
//                         <Box
//                           sx={{
//                             mt: 1.25,
//                             display: "grid",
//                             gridTemplateColumns: `repeat(${Math.min(galleryImages.length, 4)}, minmax(0, 1fr))`,
//                             gap: 1,
//                           }}
//                         >
//                           {galleryImages.map((image, index) => (
//                             <Box
//                               key={`${image.URI}-${index}`}
//                               onClick={() => setSelectedImageIndex(index)}
//                               sx={{
//                                 height: 82,
//                                 cursor: "pointer",
//                                 borderRadius: "10px",
//                                 overflow: "hidden",
//                                 border:
//                                   index === selectedImageIndex
//                                     ? `2px solid ${style.color2}`
//                                     : "2px solid transparent",
//                                 backgroundColor:
//                                   index === selectedImageIndex
//                                     ? "rgba(255,255,255,0.06)"
//                                     : "rgba(0,0,0,0.18)",
//                                 "&:hover": { borderColor: style.color3 },
//                               }}
//                             >
//                               <Box
//                                 component="img"
//                                 src={image.URI}
//                                 alt={image.altText || `${productData.name} preview ${index + 1}`}
//                                 sx={{
//                                   width: "100%",
//                                   height: "100%",
//                                   objectFit: "contain",
//                                   display: "block",
//                                   p: 0.75,
//                                 }}
//                               />
//                             </Box>
//                           ))}
//                         </Box>
//                       )}
//                     </Paper>

//                     {productData.audioURIs?.[0] && (
//                       <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
//                         <Stack spacing={1}>
//                           <Stack direction="row" spacing={1} alignItems="center">
//                             <HeadphonesRoundedIcon sx={{ color: style.color3 }} />
//                             <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
//                               Audio Demo
//                             </Typography>
//                           </Stack>
//                           <audio controls style={{ width: "100%", height: "36px" }}>
//                             <source src={productData.audioURIs[0]} type="audio/mpeg" />
//                           </audio>
//                         </Stack>
//                       </Paper>
//                     )}

//                     <Paper elevation={0} sx={{ ...panelSx, p: 1.5 }}>
//                       <Stack spacing={1.25}>
//                         <Stack direction="row" spacing={1} alignItems="center">
//                           <DownloadRoundedIcon sx={{ color: style.color3 }} />
//                           <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
//                             Download Information
//                           </Typography>
//                         </Stack>

//                         <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
//                           Availability: In stock. This product is delivered as an instant digital download.
//                         </Typography>

//                         {productData.price > 0 && (
//                           <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.25 }}>
//                             <Stack spacing={1}>
//                               <Stack direction="row" spacing={1} alignItems="flex-start">
//                                 <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
//                                 <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
//                                   Download the plugin installer for your device here.
//                                 </Typography>
//                               </Stack>

//                               <Stack direction="row" spacing={1} alignItems="flex-start">
//                                 <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
//                                 <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
//                                   To obtain a license, purchase the plugin or start a subscription free trial (can be cancelled any time).
//                                 </Typography>
//                               </Stack>

//                               <Stack direction="row" spacing={1} alignItems="flex-start">
//                                 <CheckCircleIcon sx={{ color: style.color3, fontSize: "1.1rem", mt: "2px" }} />
//                                 <Typography sx={{ color: style.color3, lineHeight: 1.55, fontSize: "0.92rem" }}>
//                                   After installation, open the plugin in your DAW and sign in with your email address and password to unlock it.
//                                 </Typography>
//                               </Stack>
//                             </Stack>
//                           </Paper>
//                         )}

//                         <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
//                           {productData.windowsDownloadURI && (
//                             <Button
//                               component="a"
//                               href={productData.windowsDownloadURI}
//                               startIcon={<WindowsRoundedIcon />}
//                               sx={{
//                                 borderRadius: "5px",
//                                 textTransform: "none",
//                                 fontWeight: 700,
//                                 color: style.color4,
//                                 backgroundColor: "rgba(255,255,255,0.05)",
//                                 border: "1px solid rgba(255,255,255,0.10)",
//                                 "&:hover": {
//                                   backgroundColor: "rgba(255,255,255,0.10)",
//                                 },
//                                 flex: 1,
//                               }}
//                             >
//                               Windows Download
//                             </Button>
//                           )}

//                           {productData.macDownloadURI && (
//                             <Button
//                               component="a"
//                               href={productData.macDownloadURI}
//                               startIcon={<AppleIcon />}
//                               sx={{
//                                 borderRadius: "5px",
//                                 textTransform: "none",
//                                 fontWeight: 700,
//                                 color: style.color4,
//                                 backgroundColor: "rgba(255,255,255,0.05)",
//                                 border: "1px solid rgba(255,255,255,0.10)",
//                                 "&:hover": {
//                                   backgroundColor: "rgba(255,255,255,0.10)",
//                                 },
//                                 flex: 1,
//                               }}
//                             >
//                               Mac Download
//                             </Button>
//                           )}
//                         </Stack>
//                       </Stack>
//                     </Paper>
//                   </Stack>

//                   <Stack spacing={1.5}>
//                     <Paper elevation={0} sx={{ ...panelSx, p: 1.75 }}>
//                       <Stack spacing={1}>
//                         <Chip
//                           label={productData.price > 0 ? "Premium Plugin" : "Free Plugin"}
//                           sx={{
//                             alignSelf: "flex-start",
//                             borderRadius: "5px",
//                             color: style.color4,
//                             fontWeight: 700,
//                             backgroundColor:
//                               productData.price > 0
//                                 ? "rgba(255,255,255,0.06)"
//                                 : "rgba(0,180,60,0.18)",
//                             border:
//                               productData.price > 0
//                                 ? "1px solid rgba(255,255,255,0.08)"
//                                 : "1px solid rgba(0,200,80,0.35)",
//                           }}
//                         />

//                         <Typography
//                           variant="h1"
//                           sx={{
//                             fontSize: { xs: "2rem", md: "2.65rem" },
//                             lineHeight: 1.04,
//                             fontWeight: 800,
//                             color: style.color4,
//                             letterSpacing: "-0.03em",
//                           }}
//                         >
//                           {productData.name}
//                         </Typography>

//                         <Typography
//                           sx={{
//                             color: style.color3,
//                             fontSize: { xs: "1rem", md: "1.03rem" },
//                             lineHeight: 1.7,
//                           }}
//                         >
//                           {productData.shortDescription}
//                         </Typography>

//                         <Box
//                           component={RouterLink}
//                           to={`/reviews/${productData.productURI}`}
//                           sx={{
//                             display: "inline-flex",
//                             alignItems: "center",
//                             gap: 1,
//                             flexWrap: "wrap",
//                             width: "fit-content",
//                             textDecoration: "none",
//                             color: "inherit",
//                             pt: 0.25,
//                           }}
//                         >
//                           <Rating value={productData.starRating || 0} precision={0.5} readOnly />
//                           <Typography sx={{ fontWeight: 700, color: style.color4 }}>
//                             {productData.ratingCount > 0 ? productData.starRating : ""}
//                           </Typography>
//                           <Typography sx={{ color: style.color3 }}>
//                             ({productData.ratingCount} review{productData.ratingCount === 1 ? "" : "s"})
//                           </Typography>
//                         </Box>
//                       </Stack>

//                       <Divider sx={{ my: 1.5, backgroundColor: "rgba(255,255,255,0.08)" }} />

//                       <Stack spacing={1.5}>
//                         <Chip
//                           label="IN STOCK • INSTANT DOWNLOAD"
//                           size="small"
//                           sx={{
//                             alignSelf: "flex-start",
//                             color: style.color4,
//                             backgroundColor: "rgba(0,180,60,0.18)",
//                             border: "1px solid rgba(0,200,80,0.35)",
//                             fontWeight: 700,
//                             letterSpacing: 0.2,
//                           }}
//                         />

//                         {productData.price > 0 ? (
//                           <>
//                             <Typography sx={{ color: style.color4, fontWeight: 700 }}>
//                               Purchase this plugin
//                             </Typography>
//                             <Stack spacing={0.8}>
//                                 {hasPromoDiscount && (
//                                   <Chip
//                                     label={`FLASH SALE - Save ${savingsPercent}%`}
//                                     size="small"
//                                     sx={{
//                                       alignSelf: "flex-start",
//                                       color: "#111",
//                                       backgroundColor: "#ffcc4d",
//                                       border: "1px solid rgba(255,255,255,0.18)",
//                                       fontWeight: 900,
//                                       letterSpacing: 0.2,
//                                     }}
//                                   />
//                                 )}

//                                 <Stack direction="row" spacing={1.25} alignItems="baseline" flexWrap="wrap">
//                                   <Typography
//                                     sx={{
//                                       color: style.color3,
//                                       textDecoration: "line-through",
//                                       fontSize: "1.05rem",
//                                     }}
//                                   >
//                                     ${hasPromoDiscount ? originalPrice.toFixed(2) : Number(productData.msrp).toFixed(2)}
//                                   </Typography>

//                                   <Typography
//                                     sx={{
//                                       fontSize: "2rem",
//                                       fontWeight: 900,
//                                       color: style.color4,
//                                       lineHeight: 1,
//                                     }}
//                                   >
//                                     ${finalPrice.toFixed(2)}
//                                   </Typography>
//                                 </Stack>

//                                 {hasPromoDiscount && (
//                                   <Typography sx={{ color: style.color3, fontSize: "0.92rem", lineHeight: 1.45 }}>
//                                     Sale price applied automatically at checkout.
//                                   </Typography>
//                                 )}
//                               </Stack>

//                             {/* <Stack direction="row" spacing={1.5} alignItems="baseline" flexWrap="wrap">
//                               <Typography
//                                 sx={{
//                                   color: style.color3,
//                                   textDecoration: "line-through",
//                                   fontSize: "1.15rem",
//                                 }}
//                               >
//                                 ${Number(productData.msrp).toFixed(2)}
//                               </Typography>
//                               <Typography
//                                 sx={{
//                                   fontSize: "2.1rem",
//                                   fontWeight: 800,
//                                   color: style.color4,
//                                   lineHeight: 1,
//                                 }}
//                               >
//                                 ${Number(productData.price).toFixed(2)}
//                               </Typography>
//                             </Stack> */}

//                             <Typography sx={{ color: style.color3, lineHeight: 1.65 }}>
//                               One-time purchase. Add this plugin to your cart and own it permanently.
//                             </Typography>

//                             <Button
//                               onClick={addProductToCart}
//                               startIcon={<ShoppingCartRoundedIcon />}
//                               sx={{
//                                 alignSelf: "flex-start",
//                                 borderRadius: "5px",
//                                 textTransform: "none",
//                                 fontWeight: 700,
//                                 color: style.color4,
//                                 backgroundColor: style.accentGreen,
//                                 px: 2.5,
//                                 "&:hover": {
//                                   backgroundColor: style.accentGreen,
//                                   filter: "brightness(1.05)",
//                                 },
//                               }}
//                             >
//                               Add to Cart
//                             </Button>

//                             <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

//                             <Stack spacing={0.8}>
//                               <Stack direction="row" spacing={1} alignItems="center">
//                                 <AutoAwesomeRoundedIcon sx={{ color: style.color3 }} />
//                                 <Typography sx={{ color: style.color4, fontWeight: 700 }}>
//                                   Subscription
//                                 </Typography>
//                               </Stack>

//                               <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
//                                 Try it free for 7 days with an all access subscription.
//                               </Typography>

//                               <Button
//                                 href='/subscribe'
//                                 startIcon={<BoltRoundedIcon />}
//                                 sx={{
//                                   alignSelf: "flex-start",
//                                   borderRadius: "5px",
//                                   textTransform: "none",
//                                   fontWeight: 700,
//                                   color: style.color4,
//                                   backgroundColor: "rgba(255,255,255,0.05)",
//                                   border: "1px solid rgba(255,255,255,0.10)",
//                                   px: 2.5,
//                                   "&:hover": {
//                                     backgroundColor: "rgba(255,255,255,0.10)",
//                                   },
//                                 }}
//                               >
//                                 Try FREE for 7 Days
//                               </Button>

//                               <Typography sx={{ color: style.color3, fontSize: "0.92rem" }}>
//                                 $9.95 per month for access to all plugins. Cancel anytime.
//                               </Typography>
//                             </Stack>
//                           </>
//                         ) : (
//                           <>
//                             <Typography
//                               sx={{
//                                 fontWeight: 900,
//                                 fontSize: "2.2rem",
//                                 color: "rgb(30,170,60)",
//                                 lineHeight: 1,
//                               }}
//                             >
//                               FREE
//                             </Typography>

//                             <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
//                               Download this VST3 plugin for Mac or Windows.
//                             </Typography>

//                             <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
//                               {productData.windowsDownloadURI && (
//                                 <Button
//                                   onClick={downloadWindowsVST}
//                                   startIcon={<WindowsRoundedIcon />}
//                                   sx={{
//                                     borderRadius: "5px",
//                                     textTransform: "none",
//                                     fontWeight: 700,
//                                     color: style.color4,
//                                     backgroundColor: style.accentGreen,
//                                     "&:hover": {
//                                       backgroundColor: style.accentGreen,
//                                       filter: "brightness(1.05)",
//                                     },
//                                   }}
//                                 >
//                                   Download for Windows
//                                 </Button>
//                               )}

//                               {productData.macDownloadURI && (
//                                 <Button
//                                   onClick={downloadMacVST}
//                                   startIcon={<AppleIcon />}
//                                   sx={{
//                                     borderRadius: "5px",
//                                     textTransform: "none",
//                                     fontWeight: 700,
//                                     color: style.color4,
//                                     backgroundColor: "rgba(255,255,255,0.05)",
//                                     border: "1px solid rgba(255,255,255,0.10)",
//                                     "&:hover": {
//                                       backgroundColor: "rgba(255,255,255,0.10)",
//                                     },
//                                   }}
//                                 >
//                                   Download for Mac
//                                 </Button>
//                               )}
//                             </Stack>
//                           </>
//                         )}
//                       </Stack>
//                     </Paper>
//                   </Stack>
//                 </Box>
//               )}

//               <Box sx={{ mt: 1.5 }}>
//                 <Box
//                   sx={{
//                     display: "grid",
//                     gridTemplateColumns: { xs: "1fr", md: "1.2fr 0.8fr" },
//                     gap: 2,
//                     alignItems: "stretch",
//                   }}
//                 >
//                   <Paper elevation={0} sx={{ ...panelSx, p: { xs: 1.5, md: 2 } }}>
//                     <Stack spacing={1.5}>
//                       <Stack direction="row" spacing={1} alignItems="center">
//                         <LibraryMusicRoundedIcon sx={{ color: style.color3 }} />
//                         <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: style.color4 }}>
//                           Overview
//                         </Typography>
//                       </Stack>

//                       <Typography
//                         sx={{
//                           color: style.color4,
//                           lineHeight: 1.75,
//                           whiteSpace: "pre-line",
//                           fontSize: "1rem",
//                         }}
//                       >
//                         {productData.longDescription}
//                       </Typography>

//                       {bulletPoints.length > 0 && (
//                         <>
//                           <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

//                           <Stack spacing={1}>
//                             <Stack direction="row" spacing={1} alignItems="center">
//                               <InfoOutlinedIcon sx={{ color: style.color3 }} />
//                               <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}>
//                                 Highlights
//                               </Typography>
//                             </Stack>

//                             <Box
//                               sx={{
//                                 display: "grid",
//                                 gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
//                                 gap: 1.1,
//                               }}
//                             >
//                               {bulletPoints.map((point, idx) => (
//                                 <Paper
//                                   key={idx}
//                                   elevation={0}
//                                   sx={{
//                                     ...subtlePanelSx,
//                                     p: 1.15,
//                                     minHeight: 25,
//                                     display: "flex",
//                                     alignItems: "flex-start",
//                                   }}
//                                 >
//                                   <Stack direction="row" spacing={1} alignItems="flex-start">
//                                     <CheckCircleRoundedIcon
//                                       sx={{
//                                         color: style.color3,
//                                         mt: "2px",
//                                         fontSize: "1.05rem",
//                                         flexShrink: 0,
//                                       }}
//                                     />
//                                     <Typography
//                                       sx={{
//                                         color: style.color4,
//                                         lineHeight: 1.5,
//                                         fontSize: "0.92rem",
//                                         wordBreak: "break-word",
//                                       }}
//                                     >
//                                       {point}
//                                     </Typography>
//                                   </Stack>
//                                 </Paper>
//                               ))}
//                             </Box>
//                           </Stack>
//                         </>
//                       )}
//                     </Stack>
//                   </Paper>

//                   <Paper elevation={0} sx={{ ...panelSx, p: { xs: 1.5, md: 2 } }}>
//                     <Stack spacing={1.5}>
//                       <Stack direction="row" spacing={1} alignItems="center">
//                         <TuneRoundedIcon sx={{ color: style.color3 }} />
//                         <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: style.color4 }}>
//                           Controls
//                         </Typography>
//                       </Stack>

//                       {productData.controls?.length > 0 ? (
//                         <Stack spacing={1.1}>
//                           {productData.controls.map((control, index) => (
//                             <Card
//                               key={index}
//                               elevation={0}
//                               sx={{
//                                 backgroundColor: "rgba(0,0,0,0.18)",
//                                 color: style.color4,
//                                 borderRadius: "12px",
//                                 border: "1px solid rgba(255,255,255,0.06)",
//                                 boxShadow: "none",
//                               }}
//                             >
//                               <CardContent sx={{ p: "14px !important" }}>
//                                 <Typography
//                                   sx={{
//                                     fontWeight: 800,
//                                     color: style.color4,
//                                     mb: 0.4,
//                                   }}
//                                 >
//                                   {control.name}
//                                 </Typography>
//                                 <Typography
//                                   sx={{
//                                     color: style.color3,
//                                     lineHeight: 1.55,
//                                     fontSize: "0.92rem",
//                                   }}
//                                 >
//                                   {control.description}
//                                 </Typography>
//                               </CardContent>
//                             </Card>
//                           ))}
//                         </Stack>
//                       ) : (
//                         <Typography sx={{ color: style.color3 }}>
//                           No control information available.
//                         </Typography>
//                       )}
//                     </Stack>
//                   </Paper>
//                 </Box>
//               </Box>

//               <Divider sx={{ my: 2, backgroundColor: "rgba(255,255,255,0.08)" }} />

//               <Stack
//                 direction={{ xs: "column", md: "row" }}
//                 spacing={1.5}
//                 justifyContent="space-between"
//                 alignItems={{ xs: "flex-start", md: "center" }}
//               >
//                 <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
//                   <Typography sx={{ color: style.color3, fontSize: "0.92rem" }}>
//                     Format:
//                   </Typography>
//                   <Chip
//                     label="VST3"
//                     size="small"
//                     sx={{
//                       backgroundColor: "rgba(255,255,255,0.08)",
//                       color: style.color4,
//                     }}
//                   />
//                   <Chip
//                     label="Mac"
//                     size="small"
//                     sx={{
//                       backgroundColor: "rgba(255,255,255,0.08)",
//                       color: style.color4,
//                     }}
//                   />
//                   <Chip
//                     label="Windows"
//                     size="small"
//                     sx={{
//                       backgroundColor: "rgba(255,255,255,0.08)",
//                       color: style.color4,
//                     }}
//                   />
//                 </Stack>

//                 <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
//                   <Button
//                     component={RouterLink}
//                     to={`/reviews/${productData.productURI}`}
//                     variant="text"
//                     sx={{
//                       color: style.color3,
//                       fontWeight: 700,
//                       px: 0,
//                       "&:hover": {
//                         backgroundColor: "transparent",
//                         color: style.color4,
//                       },
//                     }}
//                   >
//                     Read all reviews
//                   </Button>

//                   <Button
//                     component={RouterLink}
//                     to="/plugins"
//                     variant="text"
//                     sx={{
//                       color: style.color3,
//                       fontWeight: 700,
//                       px: 0,
//                       "&:hover": {
//                         backgroundColor: "transparent",
//                         color: style.color4,
//                       },
//                     }}
//                   >
//                     Browse all plugins
//                   </Button>
//                 </Stack>
//               </Stack>
//             </Box>
//           </Paper>
//         </Box>
//       </Box>

//       <LoginRegisterModal
//         isOpen={isLoginRegisterModalOpen}
//         close={closeModal(setLoginRegisterModalOpen)}
//         loginComplete={loginCallback}
//       />
//     </>
//   );
// }

// import React, { useState, useContext, useEffect, useMemo, useRef } from "react";
// import axios from "axios";
// import { Link as RouterLink, useParams } from "react-router-dom";
// import { UserContext } from "../../context/userContext/userContext";
// import { useStyle } from "../../context/styleContext/styleContext";
// import { ShoppingCartContext } from "../../context/shoppingCartContext/shoppingCartContext";
// import { toast } from "react-hot-toast";
// import ReactGA from "react-ga4";
// import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
// import Seo from "../../components/Seo";
// import LoginRegisterModal from "./LoginRegisterModal";
// import ProductSchemaMarkup from "./ProductSchemaMarkup";
// import {
//   Box,
//   Typography,
//   Button,
//   useMediaQuery,
//   Rating,
//   Divider,
//   Stack,
//   Paper,
//   Chip,
//   IconButton,
//   Card,
//   CardContent,
//   Grid,
// } from "@mui/material";
// import { useTheme } from "@mui/material/styles";
// import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
// import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
// import WindowsRoundedIcon from "@mui/icons-material/DesktopWindowsRounded";
// import AppleIcon from "@mui/icons-material/Apple";
// import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
// import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
// import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
// import HeadphonesRoundedIcon from "@mui/icons-material/HeadphonesRounded";
// import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
// import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
// import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
// import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
// import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// export default function ProductPage() {
//   const { user } = useContext(UserContext);
//   const { style } = useStyle();
//   const { addToCart } = useContext(ShoppingCartContext);
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("md"));
//   const { sku } = useParams();

//   const [productData, setProductData] = useState(null);
//   const [forwardToSubscribePage, setForwardToSubscribePage] = useState(false);
//   const [windowsDownloadRequested, setWindowsDownloadRequested] = useState(false);
//   const [macDownloadRequested, setMacDownloadRequested] = useState(false);
//   const [isLoginRegisterModalOpen, setLoginRegisterModalOpen] = useState(false);
//   const [height, setHeight] = useState(window.innerHeight);
//   const [selectedImageIndex, setSelectedImageIndex] = useState(0);

//   useEffect(() => {
//     ReactGA.send({ hitType: "pageview", page: sku, title: sku });
//     getProductPageInfo();
//   }, [sku]);

//   useEffect(() => {
//     function handleResize() {
//       setHeight(window.innerHeight);
//     }
//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   useEffect(() => {
//     setSelectedImageIndex(0);
//   }, [productData?.productURI]);

//   useEffect(() => {
//     window.scrollTo({ top: 0, left: 0, behavior: "auto" });
//   }, [sku]);

//   const bulletPoints = useMemo(() => {
//     if (!productData) return [];
//     return [
//       productData.bulletPoint1,
//       productData.bulletPoint2,
//       productData.bulletPoint3,
//       productData.bulletPoint4,
//       productData.bulletPoint5,
//     ].filter(Boolean);
//   }, [productData]);

//   const galleryImages = useMemo(() => {
//     if (!productData?.images) return [];
//     const valid = productData.images.filter((img) => img && img.URI);
//     if (valid.length <= 1) return valid;
//     return valid.slice(0, -1);
//   }, [productData]);

//   const selectedImage = galleryImages[selectedImageIndex] || galleryImages[0];

//   const closeModal = (setModalFn) => () => setModalFn(false);

//   const getProductPageInfo = async () => {
//     try {
//       const { data } = await axios.post("/get_product_page_info", { productURI: sku });
//       if (data) {
//         setProductData(data);
//       }
//     } catch (error) {
//       console.log("Error retrieving data...", error);
//     }
//   };

//   const addProductToCart = (e) => {
//     e.preventDefault();
//     addToCart(productData.SKU);
//     toast.success(`${productData.name} added to cart`);
//   };

//   const downloadWindowsVST = async () => {
//     const token = localStorage.getItem("token");

//     if (!user) {
//       setWindowsDownloadRequested(true);
//       setLoginRegisterModalOpen(true);
//       return;
//     }

//     try {
//       await axios.post("/api/register_free_product", {
//         token,
//         sku: productData.SKU,
//         productVersion: productData.productVersion,
//       });

//       window.location.href = productData.windowsDownloadURI;
//     } catch (error) {
//       toast.error("Error: your request could not be completed at this time.");
//     }
//   };

//   const downloadMacVST = async () => {
//     const token = localStorage.getItem("token");

//     if (!user) {
//       setMacDownloadRequested(true);
//       setLoginRegisterModalOpen(true);
//       return;
//     }

//     try {
//       await axios.post("/api/register_free_product", {
//         token,
//         sku: productData.SKU,
//         productVersion: productData.productVersion,
//       });

//       window.location.href = productData.macDownloadURI;
//     } catch (error) {
//       toast.error("Error: your request could not be completed at this time.");
//     }
//   };

//   const navToSubscribePage = () => {
//     if (user) {
//       window.location.href = `https://buy.stripe.com/6oE3gfaAxdanaticMM?prefilled_email=${encodeURIComponent(
//         user.email
//       )}`;
//     } else {
//       setForwardToSubscribePage(true);
//       setLoginRegisterModalOpen(true);
//     }
//   };

//   const loginCallback = async (userEmail) => {
//     const token = localStorage.getItem("token");
//     if (!token || !productData) return;

//     setLoginRegisterModalOpen(false);

//     if (forwardToSubscribePage && userEmail) {
//       setForwardToSubscribePage(false);
//       window.location.href = `https://buy.stripe.com/6oE3gfaAxdanaticMM?prefilled_email=${encodeURIComponent(
//         userEmail
//       )}`;
//       return;
//     }

//     try {
//       if (windowsDownloadRequested) {
//         setWindowsDownloadRequested(false);
//         await axios.post("/api/register_free_product", {
//           token,
//           sku: productData.SKU,
//           productVersion: productData.productVersion,
//         });
//         window.location.href = productData.windowsDownloadURI;
//       }

//       if (macDownloadRequested) {
//         setMacDownloadRequested(false);
//         await axios.post("/api/register_free_product", {
//           token,
//           sku: productData.SKU,
//           productVersion: productData.productVersion,
//         });
//         window.location.href = productData.macDownloadURI;
//       }
//     } catch (error) {
//       toast.error("Error: your request could not be completed at this time.");
//     }
//   };

//   const nextImage = () => {
//     if (galleryImages.length < 2) return;
//     setSelectedImageIndex((prev) => (prev + 1) % galleryImages.length);
//   };

//   const previousImage = () => {
//     if (galleryImages.length < 2) return;
//     setSelectedImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
//   };

//   if (!productData) {
//     return (
//       <>
//         <Seo
//           title="Parish Audio Plugin"
//           description="Professional audio plugins by Parish Audio."
//           canonical="https://parishaudio.com/products"
//         />
//         <Box sx={{ minHeight: height - 130 }}>
//           <LoadingSpinner />
//         </Box>
//       </>
//     );
//   }

//   const titleBlock = (
//     <Stack spacing={1}>
//       <Chip
//         label={productData.price > 0 ? "Premium Plugin" : "Free Plugin"}
//         sx={{
//           alignSelf: "flex-start",
//           color: style.color4,
//           backgroundColor:
//             productData.price > 0 ? "rgba(255,255,255,0.08)" : "rgba(0,180,60,0.18)",
//           border:
//             productData.price > 0
//               ? "1px solid rgba(255,255,255,0.08)"
//               : "1px solid rgba(0,200,80,0.35)",
//         }}
//       />

//       <Typography
//         variant="h1"
//         sx={{
//           fontSize: { xs: "2rem", md: "2.5rem" },
//           lineHeight: 1.06,
//           fontWeight: 800,
//           color: style.color4,
//         }}
//       >
//         {productData.name}
//       </Typography>

//       <Typography
//         variant="body1"
//         sx={{
//           color: style.color3,
//           fontSize: { xs: "1rem", md: "1.06rem" },
//           maxWidth: 680,
//         }}
//       >
//         {productData.shortDescription}
//       </Typography>
//     </Stack>
//   );

//   const ratingBlock = (
//     <Box
//       component={RouterLink}
//       to={`/reviews/${productData.productURI}`}
//       sx={{
//         display: "inline-flex",
//         alignItems: "center",
//         gap: 1,
//         flexWrap: "wrap",
//         width: "fit-content",
//         textDecoration: "none",
//         color: "inherit",
//       }}
//     >
//       <Rating value={productData.starRating || 0} precision={0.5} readOnly />
//       <Typography sx={{ fontWeight: 700, color: style.color4 }}>
//         {productData.ratingCount > 0 ? productData.starRating : ""}
//       </Typography>
//       <Typography sx={{ color: style.color3 }}>
//         ({productData.ratingCount} review{productData.ratingCount === 1 ? "" : "s"})
//       </Typography>
//     </Box>
//   );

//   const purchaseBlock = (
//     <Paper
//       sx={{
//         p: 2,
//         borderRadius: 3,
//         background:
//           "linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(0,0,0,0.18) 100%)",
//         boxShadow: 8,
//         border: `1px solid rgba(255,255,255,0.06)`,
//       }}
//     >
//       {productData.price > 0 ? (
//         <Stack spacing={2}>
//           <Stack spacing={0.7}>
//             <Typography
//               sx={{
//                 color: style.color4,
//                 fontSize: "1rem",
//                 fontWeight: 700,
//                 letterSpacing: 0.2,
//               }}
//             >
//               Purchase this plugin
//             </Typography>

//             <Stack direction="row" spacing={1.5} alignItems="baseline" flexWrap="wrap">
//               <Typography
//                 sx={{
//                   color: style.color3,
//                   textDecoration: "line-through",
//                   fontSize: "1.15rem",
//                 }}
//               >
//                 ${Number(productData.msrp).toFixed(2)}
//               </Typography>
//               <Typography
//                 sx={{
//                   fontSize: "2rem",
//                   fontWeight: 800,
//                   color: style.color4,
//                 }}
//               >
//                 ${Number(productData.price).toFixed(2)}
//               </Typography>
//             </Stack>

//             <Typography variant="body2" sx={{ color: style.color3 }}>
//               One-time purchase. Add this plugin to your cart and own it permanently.
//             </Typography>

//             <Button
//               onClick={addProductToCart}
//               startIcon={<ShoppingCartRoundedIcon />}
//               sx={{
//                 alignSelf: "flex-start",
//                 backgroundColor: style.color2,
//                 color: style.color4,
//                 fontWeight: 700,
//                 px: 2.25,
//                 py: 1.2,
//                 boxShadow: 8,
//                 mt: 0.5,
//                 minWidth:'200px',
//                 "&:hover": {
//                   filter: "brightness(1.12)",
//                   backgroundColor: style.color2,
//                 },
//               }}
//             >
//               Add to Cart
//             </Button>
//           </Stack>

//           <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

//           <Stack spacing={0.8}>
//             <Stack direction="row" spacing={1} alignItems="center">
//               <AutoAwesomeRoundedIcon sx={{ color: style.color2 }} />
//               <Typography
//                 sx={{
//                   fontSize: "1rem",
//                   fontWeight: 700,
//                   color: style.color4,
//                 }}
//               >
//                 Subscribe
//               </Typography>
//             </Stack>

//             <Button
//               onClick={navToSubscribePage}
//               variant="outlined"
//               sx={{
//                 alignSelf: "flex-start",
//                 borderColor: style.color2,
//                 color: style.color4,
//                 fontWeight: 700,
//                 minWidth:'200px',
//                 px: 2.25,
//                 py: 1.2,
//                 "&:hover": {
//                   borderColor: style.color3,
//                   backgroundColor: "rgba(255,255,255,0.04)",
//                 },
//               }}
//             >
//               Try FREE for 7 Days
//             </Button>

//             <Typography variant="body2" sx={{ color: style.color3 }}>
//               $9.95 per month for access to all plugins. Cancel anytime.
//             </Typography>
//           </Stack>
//         </Stack>
//       ) : (
//         <Stack spacing={1.25}>
//           <Typography
//             sx={{
//               fontWeight: 900,
//               fontSize: { xs: "2rem", md: "2.2rem" },
//               color: "rgb(30,170,60)",
//               lineHeight: 1,
//             }}
//           >
//             FREE
//           </Typography>

//           <Typography variant="body1" sx={{ color: style.color3 }}>
//             Download this VST3 plugin for Mac or Windows.
//           </Typography>

//           <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
//             {productData.windowsDownloadURI && (
//               <Button
//                 onClick={downloadWindowsVST}
//                 startIcon={<WindowsRoundedIcon />}
//                 sx={{
//                   backgroundColor: style.color2,
//                   color: style.color4,
//                   fontWeight: 700,
//                   px: 2.25,
//                   py: 1.15,
//                   boxShadow: 8,
//                   "&:hover": {
//                     filter: "brightness(1.12)",
//                     backgroundColor: style.color2,
//                   },
//                 }}
//               >
//                 Download for Windows
//               </Button>
//             )}

//             {productData.macDownloadURI && (
//               <Button
//                 onClick={downloadMacVST}
//                 startIcon={<AppleIcon />}
//                 variant="outlined"
//                 sx={{
//                   borderColor: style.color2,
//                   color: style.color4,
//                   fontWeight: 700,
//                   px: 2.25,
//                   py: 1.15,
//                   "&:hover": {
//                     borderColor: style.color3,
//                     backgroundColor: "rgba(255,255,255,0.04)",
//                   },
//                 }}
//               >
//                 Download for Mac
//               </Button>
//             )}
//           </Stack>
//         </Stack>
//       )}
//     </Paper>
//   );

//   const galleryBlock = (
//     <Paper
//       sx={{
//         position: "relative",
//         p: { xs: 1.25, md: 1.5 },
//         borderRadius: 3,
//         background:
//           "linear-gradient(180deg, rgba(255,255,255,0.045) 0%, rgba(0,0,0,0.18) 100%)",
//         boxShadow: 8,
//         border: `1px solid rgba(255,255,255,0.08)`,
//       }}
//     >
//       <Box
//         sx={{
//           minHeight: { xs: 260, sm: 320, md: 420 },
//           height: { xs: 260, sm: 320, md: 420 },
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "center",
//           backgroundColor: "rgba(0,0,0,0.22)",
//           borderRadius: 2.5,
//           overflow: "hidden",
//           position: "relative",
//         }}
//       >
//         {galleryImages.length > 1 && (
//           <>
//             <IconButton
//               onClick={previousImage}
//               sx={{
//                 position: "absolute",
//                 left: 10,
//                 zIndex: 2,
//                 backgroundColor: "rgba(0,0,0,0.35)",
//                 color: style.color4,
//                 "&:hover": { backgroundColor: "rgba(0,0,0,0.5)" },
//               }}
//             >
//               <ArrowBackIosNewRoundedIcon fontSize="small" />
//             </IconButton>

//             <IconButton
//               onClick={nextImage}
//               sx={{
//                 position: "absolute",
//                 right: 10,
//                 zIndex: 2,
//                 backgroundColor: "rgba(0,0,0,0.35)",
//                 color: style.color4,
//                 "&:hover": { backgroundColor: "rgba(0,0,0,0.5)" },
//               }}
//             >
//               <ArrowForwardIosRoundedIcon fontSize="small" />
//             </IconButton>
//           </>
//         )}

//         <Box
//           component="img"
//           src={selectedImage?.URI}
//           alt={selectedImage?.altText || productData.name}
//           sx={{
//             maxWidth: "100%",
//             maxHeight: "100%",
//             objectFit: "contain",
//             display: "block",
//             p: { xs: 1, md: 1.5 },
//           }}
//         />
//       </Box>

//       {galleryImages.length > 1 && (
//         <Box
//           sx={{
//             mt: 1.25,
//             display: "grid",
//             gridTemplateColumns: {
//               xs: `repeat(${Math.min(galleryImages.length, 3)}, minmax(0, 1fr))`,
//               sm: `repeat(${Math.min(galleryImages.length, 4)}, minmax(0, 1fr))`,
//             },
//             gap: 1,
//           }}
//         >
//           {galleryImages.map((image, index) => (
//             <Box
//               key={`${image.URI}-${index}`}
//               onClick={() => setSelectedImageIndex(index)}
//               sx={{
//                 height: { xs: 74, md: 82 },
//                 cursor: "pointer",
//                 borderRadius: 2,
//                 overflow: "hidden",
//                 border:
//                   index === selectedImageIndex
//                     ? `2px solid ${style.color2}`
//                     : "2px solid transparent",
//                 backgroundColor:
//                   index === selectedImageIndex
//                     ? "rgba(255,255,255,0.06)"
//                     : "rgba(0,0,0,0.18)",
//                 transition: "all 0.18s ease",
//                 "&:hover": {
//                   borderColor: style.color3,
//                 },
//               }}
//             >
//               <Box
//                 component="img"
//                 src={image.URI}
//                 alt={image.altText || `${productData.name} preview ${index + 1}`}
//                 sx={{
//                   width: "100%",
//                   height: "100%",
//                   objectFit: "contain",
//                   display: "block",
//                   p: 0.75,
//                 }}
//               />
//             </Box>
//           ))}
//         </Box>
//       )}
//     </Paper>
//   );

//   const downloadBlock = (
//     <Paper
//       sx={{
//         p: 2,
//         borderRadius: 3,
//         backgroundColor: "rgba(255,255,255,0.04)",
//         boxShadow: 6,
//       }}
//     >
//       <Stack spacing={1.25}>
//         <Stack direction="row" spacing={1} alignItems="center">
//           <DownloadRoundedIcon sx={{ color: style.color2 }} />
//           <Typography
//             variant="h3"
//             sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}
//           >
//             Download VST3 Plugin
//           </Typography>
//         </Stack>

//         {productData.price > 0 && (
//           <Paper
//             sx={{
//               p: 1.25,
//               borderRadius: 2,
//               backgroundColor: "rgba(0,0,0,0.18)",
//               boxShadow: 2,
//             }}
//           >
//             <Stack spacing={1}>
//               <Stack direction="row" spacing={1} alignItems="flex-start">
//                 <CheckCircleIcon sx={{ color: style.color2, fontSize: "1.1rem", mt: "2px" }} />
//                 <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.55 }}>
//                   Once you have purchased this plugin or started a free trial or subscription,
//                   you can download the installer here.
//                 </Typography>
//               </Stack>

//               <Stack direction="row" spacing={1} alignItems="flex-start">
//                 <CheckCircleIcon sx={{ color: style.color2, fontSize: "1.1rem", mt: "2px" }} />
//                 <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.55 }}>
//                   After installation, open the plugin in your DAW and sign in with your username
//                   and password to unlock it.
//                 </Typography>
//               </Stack>
//             </Stack>
//           </Paper>
//         )}

//         <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
//           {productData.windowsDownloadURI && (
//             <Button
//               component="a"
//               href={productData.windowsDownloadURI}
//               startIcon={<WindowsRoundedIcon />}
//               variant="outlined"
//               sx={{
//                 borderColor: style.color2,
//                 color: style.color4,
//                 fontWeight: 700,
//                 px: 2,
//                 py: 1.1,
//                 "&:hover": {
//                   borderColor: style.color3,
//                   backgroundColor: "rgba(255,255,255,0.04)",
//                 },
//                 flex: 1,
//               }}
//             >
//               Windows Download
//             </Button>
//           )}

//           {productData.macDownloadURI && (
//             <Button
//               component="a"
//               href={productData.macDownloadURI}
//               startIcon={<AppleIcon />}
//               variant="outlined"
//               sx={{
//                 borderColor: style.color2,
//                 color: style.color4,
//                 fontWeight: 700,
//                 px: 2,
//                 py: 1.1,
//                 "&:hover": {
//                   borderColor: style.color3,
//                   backgroundColor: "rgba(255,255,255,0.04)",
//                 },
//                 flex: 1,
//               }}
//             >
//               Mac Download
//             </Button>
//           )}
//         </Stack>
//       </Stack>
//     </Paper>
//   );

//   return (
//     <>
//       <Seo
//         title={`${productData.name} VST3 Plugin | Parish Audio`}
//         description={`${productData.name} by Parish Audio. ${productData.shortDescription} Available for Mac and Windows as a VST3 plugin.`}
//         canonical={`https://parishaudio.com/products/${productData.productURI}`}
//         image={
//           productData?.images?.[0]?.URI
//             ? `https://parishaudio.com${productData.images[0].URI}`
//             : "https://parishaudio.com/assets/og-default.jpg"
//         }
//         imageAlt={
//           productData?.images?.[0]?.altText || productData?.name || "Parish Audio plugin"
//         }
//         ogType="product"
//       />

//       <ProductSchemaMarkup productData={productData} />

//       <Box sx={{ width: "95%", maxWidth: 1240, mx: "auto", minHeight: "calc(100vh - 160px)" }}>
//         <Paper
//           sx={{
//             maxWidth: 1120,
//             mx: "auto",
//             p: { xs: 1.5, sm: 2, md: 3 },
//             mt: 2,
//             mb: 4,
//             backgroundColor: style.color1,
//             borderRadius: 3,
//             color: style.color4,
//             boxShadow: 12,
//             overflow: "hidden",
//           }}
//         >
//           {isMobile ? (
//             <Stack spacing={2.25}>
//               {titleBlock}
//               {galleryBlock}
//               {ratingBlock}
//               {purchaseBlock}
//               {downloadBlock}
//             </Stack>
//           ) : (
//             <Box
//               sx={{
//                 display: "grid",
//                 gridTemplateColumns: "minmax(360px, 0.95fr) minmax(0, 1.05fr)",
//                 gap: 3,
//                 alignItems: "start",
//               }}
//             >
//               <Stack spacing={2}>
//                 {galleryBlock}
//                 {downloadBlock}
//               </Stack>

//               <Stack spacing={2}>
//                 {titleBlock}
//                 {ratingBlock}
//                 {purchaseBlock}

//                 {productData.audioURIs?.[0] && (
//                   <Paper
//                     sx={{
//                       p: 2,
//                       borderRadius: 3,
//                       backgroundColor: "rgba(255,255,255,0.04)",
//                       boxShadow: 6,
//                     }}
//                   >
//                     <Stack spacing={1}>
//                       <Stack direction="row" spacing={1} alignItems="center">
//                         <HeadphonesRoundedIcon sx={{ color: style.color2 }} />
//                         <Typography
//                           variant="h3"
//                           sx={{ mb: 0, fontSize: "1rem", fontWeight: 700, color: style.color4 }}
//                         >
//                           Audio Demo
//                         </Typography>
//                       </Stack>
//                       <audio controls style={{ width: "100%", height: "36px" }}>
//                         <source src={productData.audioURIs[0]} type="audio/mpeg" />
//                       </audio>
//                     </Stack>
//                   </Paper>
//                 )}
//               </Stack>
//             </Box>
//           )}

//           {isMobile && productData.audioURIs?.[0] && (
//             <Paper
//               sx={{
//                 p: 2,
//                 mt: 2.25,
//                 borderRadius: 3,
//                 backgroundColor: "rgba(255,255,255,0.04)",
//                 boxShadow: 6,
//               }}
//             >
//               <Stack spacing={1}>
//                 <Stack direction="row" spacing={1} alignItems="center">
//                   <HeadphonesRoundedIcon sx={{ color: style.color2 }} />
//                   <Typography
//                     variant="h3"
//                     sx={{ mb: 0, fontSize: "1rem", fontWeight: 700, color: style.color4 }}
//                   >
//                     Audio Demo
//                   </Typography>
//                 </Stack>
//                 <audio controls style={{ width: "100%", height: "36px" }}>
//                   <source src={productData.audioURIs[0]} type="audio/mpeg" />
//                 </audio>
//               </Stack>
//             </Paper>
//           )}

//           <Grid container spacing={2.25} sx={{ mt: 0.5 }}>
//             <Grid item xs={12} md={7}>
//               <Paper
//                 sx={{
//                   p: { xs: 2, md: 2.25 },
//                   borderRadius: 3,
//                   backgroundColor: "rgba(255,255,255,0.035)",
//                   boxShadow: 8,
//                   height: "100%",
//                 }}
//               >
//                 <Stack spacing={1.5}>
//                   <Stack direction="row" spacing={1} alignItems="center">
//                     <LibraryMusicRoundedIcon sx={{ color: style.color2 }} />
//                     <Typography
//                       variant="h2"
//                       sx={{ fontWeight: 800, fontSize: "1.15rem", color: style.color4 }}
//                     >
//                       Overview
//                     </Typography>
//                   </Stack>

//                   <Typography
//                     variant="body1"
//                     sx={{
//                       color: style.color4,
//                       lineHeight: 1.72,
//                       whiteSpace: "pre-line",
//                       fontSize: "1.02rem",
//                     }}
//                   >
//                     {productData.longDescription}
//                   </Typography>

//                   {bulletPoints.length > 0 && (
//                     <>
//                       <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

//                       <Stack spacing={1}>
//                         <Stack direction="row" spacing={1} alignItems="center">
//                           <InfoOutlinedIcon sx={{ color: style.color2 }} />
//                           <Typography
//                             variant="h3"
//                             sx={{ fontSize: "1rem", fontWeight: 700, color: style.color4 }}
//                           >
//                             Highlights
//                           </Typography>
//                         </Stack>

//                         <Grid container spacing={1.1} alignItems="stretch">
//                           {bulletPoints.map((point, idx) => (
//                             <Grid item xs={12} sm={6} key={idx} sx={{ display: "flex" }}>
//                               <Paper
//                                 sx={{
//                                   p: 1.2,
//                                   width: "100%",
//                                   minHeight: 25,
//                                   borderRadius: 2.5,
//                                   backgroundColor: "rgba(255,255,255,0.035)",
//                                   boxShadow: 3,
//                                   display: "flex",
//                                   alignItems: "flex-start",
//                                 }}
//                               >
//                                 <Stack direction="row" spacing={1} alignItems="flex-start">
//                                   <CheckCircleRoundedIcon
//                                     sx={{
//                                       color: style.color2,
//                                       mt: "2px",
//                                       fontSize: "1.05rem",
//                                       flexShrink: 0,
//                                     }}
//                                   />
//                                   <Typography
//                                     variant="body2"
//                                     sx={{
//                                       color: style.color4,
//                                       lineHeight: 1.5,
//                                       wordBreak: "break-word",
//                                     }}
//                                   >
//                                     {point}
//                                   </Typography>
//                                 </Stack>
//                               </Paper>
//                             </Grid>
//                           ))}
//                         </Grid>
//                       </Stack>
//                     </>
//                   )}
//                 </Stack>
//               </Paper>
//             </Grid>

//             <Grid item xs={12} md={5}>
//               <Paper
//                 sx={{
//                   p: { xs: 2, md: 2.25 },
//                   borderRadius: 3,
//                   backgroundColor: "rgba(255,255,255,0.035)",
//                   boxShadow: 8,
//                   height: "100%",
//                 }}
//               >
//                 <Stack spacing={1.5}>
//                   <Stack direction="row" spacing={1} alignItems="center">
//                     <TuneRoundedIcon sx={{ color: style.color2 }} />
//                     <Typography
//                       variant="h2"
//                       sx={{ fontWeight: 800, fontSize: "1.15rem", color: style.color4 }}
//                     >
//                       Controls
//                     </Typography>
//                   </Stack>

//                   {productData.controls?.length > 0 ? (
//                     <Stack spacing={1.1}>
//                       {productData.controls.map((control, index) => (
//                         <Card
//                           key={index}
//                           sx={{
//                             backgroundColor: "rgba(0,0,0,0.18)",
//                             color: style.color4,
//                             borderRadius: 2.5,
//                             boxShadow: 4,
//                           }}
//                         >
//                           <CardContent sx={{ p: "14px !important" }}>
//                             <Typography
//                               variant="subtitle1"
//                               sx={{
//                                 fontWeight: 800,
//                                 color: style.color4,
//                                 mb: 0.4,
//                               }}
//                             >
//                               {control.name}
//                             </Typography>
//                             <Typography
//                               variant="body2"
//                               sx={{
//                                 color: style.color3,
//                                 lineHeight: 1.55,
//                               }}
//                             >
//                               {control.description}
//                             </Typography>
//                           </CardContent>
//                         </Card>
//                       ))}
//                     </Stack>
//                   ) : (
//                     <Typography variant="body2" sx={{ color: style.color3 }}>
//                       No control information available.
//                     </Typography>
//                   )}
//                 </Stack>
//               </Paper>
//             </Grid>
//           </Grid>

//           <Divider sx={{ my: 2.5, backgroundColor: "rgba(255,255,255,0.08)" }} />

//           <Stack
//             direction={{ xs: "column", md: "row" }}
//             spacing={1.5}
//             justifyContent="space-between"
//             alignItems={{ xs: "flex-start", md: "center" }}
//           >
//             <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
//               <Typography variant="body2" sx={{ color: style.color3 }}>
//                 Format:
//               </Typography>
//               <Chip
//                 label="VST3"
//                 size="small"
//                 sx={{
//                   backgroundColor: "rgba(255,255,255,0.08)",
//                   color: style.color4,
//                 }}
//               />
//               <Chip
//                 label="Mac"
//                 size="small"
//                 sx={{
//                   backgroundColor: "rgba(255,255,255,0.08)",
//                   color: style.color4,
//                 }}
//               />
//               <Chip
//                 label="Windows"
//                 size="small"
//                 sx={{
//                   backgroundColor: "rgba(255,255,255,0.08)",
//                   color: style.color4,
//                 }}
//               />
//             </Stack>

//             <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
//               <Button
//                 component={RouterLink}
//                 to={`/reviews/${productData.productURI}`}
//                 variant="text"
//                 sx={{
//                   color: style.color3,
//                   fontWeight: 700,
//                   px: 0,
//                   mr:2,
//                   "&:hover": {
//                     backgroundColor: "transparent",
//                     color: style.color4,
//                   },
//                 }}
//               >
//                 Read all reviews
//               </Button>
                
//               <Button
//                 component={RouterLink}
//                 to="/plugins"
//                 variant="text"
//                 sx={{
//                   color: style.color3,
//                   fontWeight: 700,
//                   px: 0,
//                   "&:hover": {
//                     backgroundColor: "transparent",
//                     color: style.color4,
//                   },
//                 }}
//               >
//                 Browse all plugins
//               </Button>
//             </Stack>
//           </Stack>
//         </Paper>
//       </Box>

//       <LoginRegisterModal
//         isOpen={isLoginRegisterModalOpen}
//         close={closeModal(setLoginRegisterModalOpen)}
//         loginComplete={loginCallback}
//       />
//     </>
//   );
// }
